/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T00:30:00+05:00
Intent: A participant whose first attempt began reads "In progress" everywhere on the page (Participants table and
filter, leaderboard, levels drawer, PDF), and the Level Matrix counts them on the level they are playing, even while
the server still stores them as "not started".
The receipt summary lists products by their Arabic name in Arabic, when one was given.
The survey export copes with full-profile answers: profile fields are read as text, building the file can't leave the loader stuck, a failed refetch falls back to the participants on the page, and a file that can't be made says so instead of "couldn't load".
The receipt summary shows the minimum spend's currency and the purchase window as dd/mm/yy.
A campaign with a receipt level gets the Receipt submissions card (image, read values, rule results, approve / reject).
*/
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SharedModule } from '@shared/shared.module';
import { PagesService } from '@services/pages.service';
import { QRCodeComponent } from 'angularx-qrcode';
import { LUQTA_WEBHOOK_URL } from 'src/app/constants/micro-api.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { v4 as uuidv4 } from 'uuid';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { FilterModalComponent, FilterConfig } from '@pages/filter-modal/filter-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { isArabicLang, ensureArabicFont, reshapeArabic, containsArabic, arabicAwareCellHook, drawBilingualText } from 'src/app/utils/arabic-pdf.util';
import { canEditCampaign, cannotEditMessageKey } from '../contests/campaign-publication';
import { KycTier, toKycTier } from 'src/app/utils/kyc-tier.util';
import { buildSurveyExport, readSurveyAnswers, SurveyRespondent, surveyExportFileName, writeSurveyWorkbook } from 'src/app/utils/survey-export.util';
import { isAcceptAnyTextLevel, TextAnswer, textLevelAnswer } from 'src/app/utils/text-answer.util';
import { isSupportedCampaignImage } from '../add-contents/campaign-validation';
import { cleanCurrencies, toIsoDate } from '../add-contents/receipt-rules';
import { countryCodeForName, currencyForCountry } from 'src/app/utils/country-currency.util';
import { loadQrInstanceIndex, QrInstanceRef, qrInstanceOfRow, qrInstanceStatusKey, readQrInstances } from 'src/app/utils/qr-instance.util';
import { resolveQrInstanceLink } from 'src/app/utils/web-qr-link.util';
import { ContestAuditRecordsComponent } from './contest-audit-records/contest-audit-records.component';
import { ContestReceiptSubmissionsComponent, ReceiptLevelOption } from './contest-receipt-submissions/contest-receipt-submissions.component';
import { GiftCardCode, GiftCardCodeStatus, GiftCardPool, isUniqueGiftCard, participantIds, readGiftCardPool, readPrizePools } from 'src/app/utils/gift-card-codes.util';
import { effectiveLevelStatuses, effectiveParticipantStatus, reconcileLevelMatrix, toProgressStatus } from 'src/app/utils/participant-status.util';

interface ContestPerformance {
  totalViews: number | null;
  completedTasks: number | null;
  completedTasksIsRate?: boolean;
  avgClickRate: number | null;
  avgCompletionRate: number | null;
  participationRate: number | null;
  totalViewsTrend?: number | null;
  completedTasksTrend?: number | null;
  avgClickRateTrend?: number | null;
  participationRateTrend?: number | null;
  totalViewsTrendLabel?: string | null;
  totalViewsTrendClass?: string | null;
  completedTasksTrendLabel?: string | null;
  completedTasksTrendClass?: string | null;
  avgClickRateTrendLabel?: string | null;
  avgClickRateTrendClass?: string | null;
  participationRateTrendLabel?: string | null;
  participationRateTrendClass?: string | null;
}

interface ContestPerson {
  id: number | string;
  name: string;
  email: string;
}

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-constant-detail',
  standalone: true,
  imports: [RouterLink, CommonModule, SharedModule, QRCodeComponent, ReactiveFormsModule, FormsModule, NzModalModule, NzSelectModule, NzTableModule, NzToolTipModule, NzDrawerModule, NzDatePickerModule, NzDropDownModule, FilterModalComponent, ContestAuditRecordsComponent, ContestReceiptSubmissionsComponent],
  templateUrl: './constant-detail.component.html',
  styleUrls: ['./constant-detail.scss']
})
export class ConstantDetailComponent implements OnInit, OnDestroy {

  contest_detail: any[] = [];
  readonly luqtaWebhookUrl = LUQTA_WEBHOOK_URL;
  http = inject(HttpClient);
  sharedService = inject(SharedService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  pagesService = inject(PagesService);
  clipboard = inject(Clipboard);
  contests_detail: any = {};
  name!: string;
  created_at!: string;
  description!: string;
  // Submission metadata shown on the Contest Details page.
  submitted_by: ContestPerson | null = null;
  submitted_via: string | null = null;
  submitted_on!: string;
  approved_by: ContestPerson | null = null;
  levels_of_contest: any[] = [];
  _contest_prize: any[] = [];
  banners: any[] = [];
  start_date!: string;
  end_date!: string;
  draw_date!: string;
  contestID: any;
  /** The contest end date as it arrived, for the countdown — see end_date below. */
  end_date_raw: any = null;
  showAllVariables: { [key: string]: boolean } = {};
  maxVariablesToShow: number = 3;
  editingLevels: { [key: string]: boolean } = {};
  levelForms: { [key: string]: FormGroup } = {};
  levelImagePreviews: { [key: string]: string | ArrayBuffer | null } = {};
  isImageModalVisible: { [key: string]: boolean } = {};
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  translateService = inject(TranslateService);

  get isArabic(): boolean {
    return localStorage.getItem('lang') === 'ar';
  }

  // Contest Performance and Participants data
  contestPerformance: ContestPerformance = {
    totalViews: null,
    completedTasks: null,
    avgClickRate: null,
    avgCompletionRate: null,
    participationRate: null,
    totalViewsTrend: null,
    completedTasksTrend: null,
    avgClickRateTrend: null,
    participationRateTrend: null,
    totalViewsTrendLabel: null,
    totalViewsTrendClass: null,
    completedTasksTrendLabel: null,
    completedTasksTrendClass: null,
    avgClickRateTrendLabel: null,
    avgClickRateTrendClass: null,
    participationRateTrendLabel: null,
    participationRateTrendClass: null
  };
  performanceStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performancePreviousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performanceTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  performanceLoading: boolean = false;
  performanceError: string | null = null;
  performanceDateRange: Date[] = [];
  performancePickerOpen: boolean = false;
  participantsList: any[] = [];
  filteredParticipants: any[] = [];

  // approved_by is whoever reviewed the campaign, so on a rejected one it is the rejecter.
  get isRejected(): boolean {
    const status = String(this.contests_detail?.review_status || this.contests_detail?.status || '');
    return status.toLowerCase() === 'rejected';
  }

  // KYC tier applies only to Web QR campaigns. The publication channel (`on_web` / web_approved)
  // says so even before any QR code exists; a campaign with a Web QR instance is one too.
  hasQrInstances: boolean = false;
  qrInstances: any[] = [];
  qrInstancesLoading: boolean = false;
  // participant/user id → the QR instance they joined through
  qrInstanceIndex = new Map<string, QrInstanceRef>();

  get isWebQrCampaign(): boolean {
    const cd = this.contests_detail;
    if (!cd) return false;
    const onWeb = cd.on_web === true || cd.on_web === 1 || cd.on_web === '1';
    const webApproved = [cd.status, cd.review_status].some((s: any) => String(s ?? '').toLowerCase() === 'web_approved');
    return onWeb || webApproved || this.hasQrInstances;
  }

  // The API returns the tier as a number (0 Light, 1 Medium); missing means Light, the form's default.
  get kycTier(): KycTier {
    return toKycTier(this.contests_detail?.kyc_tier ?? this.contests_detail?.kycTier);
  }
  participantsLoading: boolean = false;
  winnersList: any[] = [];
  winnersLoading: boolean = false;

  // Gift card prizes with one uploaded code per winner, keyed by prize id.
  giftCardPools: Record<string, GiftCardPool> = {};
  giftCardCodesLoading: boolean = false;
  giftCardCodesFailed: boolean = false;
  /** Winner id → the gift card codes handed to them. */
  private winnerGiftCards = new Map<string, GiftCardCode[]>();
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;
  surveyExporting: boolean = false;

  // Level Matrix — per-level completion + demographic breakdown
  levelMatrix: any = null;
  // The metrics as the server sent them; `levelMatrix` is this with in-progress participants counted from their levels.
  private levelMatrixFromServer: any = null;
  levelMetricsLoading: boolean = false;
  levelMetricsError: string = '';
  levelDemographicsDrawerVisible: boolean = false;
  selectedLevelMetrics: any = null;

  // Search and Filter for participants
  searchTerm: string = '';
  filterModalVisible: boolean = false;
  currentFilters: any = {};
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  filterConfig: FilterConfig = {
    showTimeline: false,
    showLevel: false,
    showStatus: true,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: true,
    // Web QR collects no gender, so the filter is gone along with the column.
    showGender: false,
    showContest: false,
    showAge: false,
    showCompletionRate: false,
    showParticipantName: false,
    showParticipantEmail: false,
    statusOptions: [
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' }
    ]
  };

  // Drawer for participant levels
  levelsDrawerVisible: boolean = false;
  selectedParticipant: any = null;
  selectedParticipantLevels: any[] = [];

  // Leaderboard
  leaderboard: any[] = [];
  leaderboardLoading: boolean = false;
  leaderboardPage: number = 1;
  leaderboardPerPage: number = 10;
  leaderboardTotal: number = 0;

  // Admin override
  overrideModalVisible: boolean = false;
  overrideParticipantId: any = null;
  overrideReason: string = '';
  overrideSubmitting: boolean = false;
  overrideAuditLog: any[] = [];

  private pdfSectionHeading(doc: jsPDF, text: string, y: number, arabicFontLoaded: boolean = false): number {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 52, 137);
    drawBilingualText(doc, text, 14, y, arabicFontLoaded);
    doc.setDrawColor(60, 52, 137);
    doc.setLineWidth(0.4);
    doc.line(14, y + 1.5, doc.internal.pageSize.width - 14, y + 1.5);
    return y + 8;
  }

  private pdfEnsureSpace(doc: jsPDF, y: number, needed: number): number {
    const pageHeight = doc.internal.pageSize.height;
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      return 15;
    }
    return y;
  }

  /** Set when the banner URL fails to load, so the heading falls back to the initial. */
  bannerLoadFailed = false;

  /** The campaign's first banner, or '' when it has none — never a stock image.
   *  The API uses `url` on some records and `path` on others. */
  get bannerUrl(): string {
    if (this.bannerLoadFailed) return '';
    const banner: any = this.banners?.[0];
    return this.sharedService.cleanImageUrl(banner?.url || banner?.path || '');
  }

  /** Prize image URLs that failed to load; those cards show the placeholder instead. */
  failedPrizeImages = new Set<string>();

  /** The prize's image, or '' when it has none (the card then shows a placeholder, not a broken image). */
  prizeImageUrl(prize: any): string {
    const url = prize?.image?.url || prize?.image?.path || '';
    return url && !this.failedPrizeImages.has(url) ? url : '';
  }

  /** Resolves the prize name based on current language (Arabic vs English) */
  getPrizeDisplayName(prize: any): string {
    if (!prize) return '';
    if (this.isArabic) {
      const arName = (prize.arabic_prize_name || prize.arabic_name || prize.arabicName || prize.name_ar || prize.prize_name_ar)?.trim();
      if (arName) return arName;
    }
    return prize.name || prize.prize_name || prize.title || '';
  }

  get campaignInitial(): string {
    return (this.name || '').trim().charAt(0) || '?';
  }

  get hasSurveyLevel(): boolean {
    return (this.levels_of_contest || []).some(
      (level: any) => (level.level_type || level.type) === 'survey'
    );
  }

  // Exports the submitted survey answers to Excel: a Summary, an All answers sheet for
  // pivot tables and one sheet per survey level, in the dashboard language.
  // `anonymous` leaves out who answered and when, and shuffles the rows.
  async exportSurveyResponses(anonymous = false): Promise<void> {
    const surveyLevels = (this.levels_of_contest || []).filter(
      (level: any) => (level.level_type || level.type) === 'survey'
    );

    if (surveyLevels.length === 0) {
      this.sharedService.createNotification('warning', 'This campaign has no survey level.');
      return;
    }

    if (this.surveyExporting) return;

    // Fetched on click: the list loaded with the page goes stale while a campaign is live,
    // and an export of answers people gave after the page opened would silently miss them.
    let participants: any[];
    this.surveyExporting = true;
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('GET_Total_PARTICIPANT', 'contest_id', this.contestID)
      );
      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.items) ? response.items : [];
      participants = items.map((participant: any) => this.normalizeContestParticipant(participant));
    } catch (error: any) {
      console.error('Survey export: loading the participants failed', error);
      // The list shown on the page came from the same request, so a failed refetch
      // still leaves something to export.
      if (error?.name === 'EmptyError' || !this.participantsList?.length) {
        this.sharedService.hideLoader();
        this.surveyExporting = false;
        this.sharedService.showError(error, this.translateService.instant('contestDetails.surveyResponsesLoadFailed'));
        return;
      }
      participants = this.participantsList;
    }

    try {
      await this.writeSurveyExport(surveyLevels, participants, anonymous);
    } catch (error) {
      // Reaching this means the answers loaded but the file couldn't be made, so "couldn't
      // load" would send the user after the wrong problem. The cause goes to the console.
      console.error('Survey export: building the file failed', error);
      this.sharedService.showError(error, this.translateService.instant('contestDetails.surveyResponsesExportFailed'));
    } finally {
      this.sharedService.hideLoader();
      this.surveyExporting = false;
    }
  }

  private async writeSurveyExport(surveyLevels: any[], participants: any[], anonymous: boolean): Promise<void> {
    // Participants who reached a survey level but came back without any answer data.
    let reachedWithoutAnswers = 0;
    // Answers the server withheld because the survey's visibility is anonymised or aggregated.
    let withheldAnswers = 0;
    const toDate = (value: any): Date | null => {
      const date = value ? new Date(value) : null;
      return date && !Number.isNaN(date.getTime()) ? date : null;
    };
    // A full profile can carry objects or numbers where text is expected; only text goes in a cell.
    const text = (...values: any[]): string => values
      .map(value => value == null || typeof value === 'object' ? '' : String(value).trim())
      .find(Boolean) || '';

    const respondents: SurveyRespondent[] = participants.map((participant: any) => {
      const user = this.getParticipantUser(participant) || {};
      const userId = user.id ?? participant?.participant_id;
      const participantLevels: any[] = participant?.contest?.levels || participant?.contest?._levels_of_contest || [];
      const submissions: SurveyRespondent['submissions'] = {};

      surveyLevels.forEach((surveyLevel: any) => {
        // Current API: per-participant `levels_detail`. Older one: `users_progress` on the level.
        const detail = (participant?.levels_detail || [])
          .find((level: any) => String(level?.id) === String(surveyLevel.id));
        const legacyLevel = participantLevels.find((level: any) => String(level?.id) === String(surveyLevel.id)) || surveyLevel;
        const progress = (legacyLevel?.users_progress || []).find((up: any) => up.user_id === userId);
        if (!detail && !progress) return;
        if (detail?.answers_withheld) {
          withheldAnswers++;
          return;
        }

        const answers = readSurveyAnswers(detail?.user_answer ?? detail?.answers ?? detail?.responses
          ?? detail?.survey_answers ?? progress?.user_answer);
        if (answers.length === 0) {
          // Submitted without answering: listed with blank answers, as in the agreed format.
          if (String(detail?.status || progress?.status).toLowerCase() !== 'completed') return;
          reachedWithoutAnswers++;
        }
        submissions[String(surveyLevel.id)] = {
          answers,
          submittedOn: toDate(detail?.completed_at ?? detail?.submitted_at ?? detail?.updated_at
            ?? progress?.completed_at ?? progress?.updated_at ?? this.getParticipationDate(participant)),
        };
      });

      const age = this.getParticipantAge(participant);
      return {
        id: userId ?? null,
        name: text(user.name, [user.first_name, user.last_name].filter(Boolean).join(' '), participant?.participant_name),
        phone: text(user.phone_number, user.phone, participant?.phone_number),
        email: text(user.email, participant?.email),
        gender: text(user.gender, participant?.gender),
        age: typeof age === 'number' || typeof age === 'string' ? age : null,
        country: text(user.country, participant?.country),
        submissions,
      };
    });

    const lang = isArabicLang() ? 'ar' : 'en';
    const data = buildSurveyExport(
      surveyLevels.map((level: any) => ({
        id: String(level.id),
        position: Number(level.position) || (this.levels_of_contest.indexOf(level) + 1),
        name: level.name || '',
        arabicName: level.arabic_name || '',
        questions: Array.isArray(level.survey_questions) ? level.survey_questions : [],
      })),
      respondents,
      { lang, anonymous }
    );

    const answered = respondents.some(person =>
      Object.values(person.submissions).some(submission => submission.answers.length > 0));
    if (!answered) {
      // Someone finished the survey but no answers came back: the server didn't send them,
      // and "nobody answered" would be wrong.
      const key = withheldAnswers > 0
        ? 'contestDetails.surveyResponsesWithheld'
        : reachedWithoutAnswers > 0
          ? 'contestDetails.surveyResponsesUnavailable'
          : 'contestDetails.noSurveyResponses';
      this.sharedService.createNotification('warning', this.translateService.instant(key));
      return;
    }

    const buffer = await writeSurveyWorkbook(data);
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const campaignName = (lang === 'ar' && this.contests_detail?.arabic_name) || this.name || String(this.contestID);
    saveAs(blob, surveyExportFileName(campaignName));
    this.sharedService.createNotification('success', this.translateService.instant('contestDetails.surveyResponsesExported'));
  }

  async exportToPDF(): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    let y = 0;

    const isAr = isArabicLang();
    const arabicFontLoaded = await ensureArabicFont(doc);

    const L = isAr ? {
      headerTitle: 'تقرير تفاصيل الحملة',
      untitled: 'حملة بدون عنوان',
      uidLabel: 'المعرف',
      accessCodeLabel: 'رمز الدخول',
      startLabel: 'البداية',
      endLabel: 'النهاية',
      drawLabel: 'السحب',
      na: 'غير متوفر',
      prizesHeading: 'الجوائز',
      prizeNameCol: 'اسم الجائزة',
      valueCol: 'القيمة',
      winnersCol: 'الفائزون',
      drawTypeCol: 'نوع السحب',
      levelsHeading: 'مستويات الحملة',
      numCol: '#',
      levelNameCol: 'اسم المستوى',
      descriptionCol: 'الوصف',
      performanceHeading: 'أداء الحملة',
      metricCol: 'المقياس',
      totalViews: 'إجمالي المشاهدات',
      totalClicks: 'إجمالي النقرات',
      participantsMetric: 'المشاركون',
      completed: 'مكتمل',
      overallSuccessRate: 'معدل النجاح الإجمالي',
      completionRate: 'معدل الإكمال',
      participationRate: 'معدل المشاركة',
      engagementRate: 'معدل الحملة',
      participantsHeading: (count: number) => `المشاركون (${count})`,
      participantCol: 'المشارك',
      participationDateCol: 'تاريخ المشاركة',
      ageCol: 'العمر',
      genderCol: 'الجنس',
      statusCol: 'الحالة',
      locationCol: 'الموقع',
      statusCompleted: 'مكتمل',
      statusNotStarted: 'لم يبدأ',
      statusInProgress: 'قيد التقدم',
      noParticipants: 'لا توجد بيانات مشاركين متاحة.',
      winnersHeading: 'الفائزون',
      rankCol: 'الترتيب',
      completionTimestampCol: 'وقت الإكمال',
      prizeStatusCol: 'حالة الجائزة',
      noWinners: 'لا توجد بيانات فائزين متاحة.',
      previewHeading: 'معاينة الحملة (تطبيق الجوال)',
      pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
      generated: (date: string) => `تم الإنشاء: ${date}`,
    } : {
      headerTitle: 'Campaign Details Report',
      untitled: 'Untitled Campaign',
      uidLabel: 'UID',
      accessCodeLabel: 'Access Code',
      startLabel: 'Start',
      endLabel: 'End',
      drawLabel: 'Draw',
      na: 'N/A',
      prizesHeading: 'Prizes',
      prizeNameCol: 'Prize Name',
      valueCol: 'Value',
      winnersCol: 'Winners',
      drawTypeCol: 'Draw Type',
      levelsHeading: 'Campaign Levels',
      numCol: '#',
      levelNameCol: 'Level Name',
      descriptionCol: 'Description',
      performanceHeading: 'Campaign Performance',
      metricCol: 'Metric',
      totalViews: 'Total Views',
      totalClicks: 'Total Clicks',
      participantsMetric: 'Participants',
      completed: 'Completed',
      overallSuccessRate: 'Overall Success Rate',
      completionRate: 'Completion Rate',
      participationRate: 'Participation Rate',
      engagementRate: 'Campaign Rate',
      participantsHeading: (count: number) => `Participants (${count})`,
      participantCol: 'Participant',
      participationDateCol: 'Participation Date',
      ageCol: 'Age',
      genderCol: 'Gender',
      statusCol: 'Status',
      locationCol: 'Location',
      statusCompleted: 'Completed',
      statusNotStarted: 'Not Started',
      statusInProgress: 'In Progress',
      noParticipants: 'No participant data available.',
      winnersHeading: 'Winners',
      rankCol: 'Rank',
      completionTimestampCol: 'Completion Timestamp',
      prizeStatusCol: 'Prize Status',
      noWinners: 'No winners data available.',
      previewHeading: 'Campaign Preview (Mobile App)',
      pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
      generated: (date: string) => `Generated: ${date}`,
    };

    // Header bar
    doc.setFillColor(60, 52, 137);
    doc.rect(0, 0, pageWidth, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, L.headerTitle, pageWidth / 2, 14, arabicFontLoaded, { align: 'center' });
    y = 32;

    // Contest preview (banner image)
    const bannerUrl = this.banners?.[0]?.url || (this.banners?.[0] as any)?.path || '';
    const previewData = bannerUrl ? await this.sharedService.getImageDataUrl(bannerUrl) : null;
    if (previewData) {
      const previewWidth = pageWidth - 28;
      const previewHeight = 55;
      try {
        doc.addImage(previewData, 'JPEG', 14, y, previewWidth, previewHeight, undefined, 'FAST');
      } catch {
        // ignore image draw failures, continue with text-only report
      }
      y += previewHeight + 8;
    }

    // Contest name
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
    y += 7;

    // UID / Access Code
    const idParts: string[] = [];
    if (this.hasValue(this.contests_detail?.uid)) idParts.push(`${L.uidLabel}: ${this.contests_detail.uid}`);
    if (this.hasValue(this.contests_detail?.access_code)) idParts.push(`${L.accessCodeLabel}: ${this.contests_detail.access_code}`);
    if (idParts.length) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(110, 110, 110);
      drawBilingualText(doc, idParts.join('   |   '), 14, y, arabicFontLoaded);
      y += 6;
    }

    // Description
    if (this.description) {
      const plainDesc = this.description.replace(/<[^>]*>/g, '').trim();
      if (plainDesc) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const lines = doc.splitTextToSize(plainDesc, pageWidth - 28);
        doc.text(lines, 14, y);
        y += lines.length * 5 + 3;
      }
    }

    // Dates row
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const fmt = (d: any) => d ? new Date(d).toLocaleString() : L.na;
    drawBilingualText(doc, `${L.startLabel}: ${fmt(this.start_date)}`, 14, y, arabicFontLoaded);
    drawBilingualText(doc, `${L.endLabel}: ${fmt(this.end_date)}`, 14, y + 5, arabicFontLoaded);
    drawBilingualText(doc, `${L.drawLabel}: ${fmt(this.draw_date)}`, 14, y + 10, arabicFontLoaded);
    y += 18;

    // Prizes
    if (this._contest_prize && this._contest_prize.length > 0) {
      y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
      const prizeRows = this._contest_prize.map((prize: any) => [
        (arabicFontLoaded ? (prize?.arabic_prize_name || prize?.arabic_name || prize?.name) : prize?.name) || L.na,
        prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        this.contests_detail?.number_of_winners != null ? String(this.contests_detail.number_of_winners) : L.na,
        this.getDrawTypeLabel ? this.getDrawTypeLabel() : (this.contests_detail?.draw_type || L.na),
      ]);
      autoTable(doc, {
        startY: y,
        head: [[L.prizeNameCol, L.valueCol, L.winnersCol, L.drawTypeCol]],
        body: prizeRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Contest Levels
    if (this.levels_of_contest && this.levels_of_contest.length > 0) {
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);

      const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
        String(i + 1),
        lvl.name || lvl.level_name || `Level ${i + 1}`,
        lvl.description || lvl.task || '',
        // No points column: Luqta allocates level points, so it is not the client's field.
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.numCol, L.levelNameCol, L.descriptionCol]],
        body: levelRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 45 }, 2: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Mobile app preview mockup
    y = this.pdfEnsureSpace(doc, y, 140);
    y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
    await this.drawMobilePreview(doc, y, isAr, arabicFontLoaded);
    y += 140;

    // Performance Overview (matches on-screen 8 metric cards)
    y = this.pdfEnsureSpace(doc, y, 60);
    y = this.pdfSectionHeading(doc, L.performanceHeading, y, arabicFontLoaded);

    autoTable(doc, {
      startY: y,
      head: [[L.metricCol, L.valueCol]],
      body: [
        [L.totalViews, String(this.performanceStats.totalContests ?? 0)],
        [L.totalClicks, String(this.performanceStats.activeContests ?? 0)],
        [L.participantsMetric, String(this.performanceStats.pendingContests ?? 0)],
        [L.completed, String(this.performanceStats.finishedContests ?? 0)],
        [L.overallSuccessRate, `${(this.performanceStats.avgClickRate ?? 0).toFixed(2)}%`],
        [L.completionRate, `${(this.performanceStats.avgCompletionRate ?? 0).toFixed(2)}%`],
        [L.participationRate, `${(this.performanceStats.avgParticipationRate ?? 0).toFixed(2)}%`],
        [L.engagementRate, `${(this.performanceStats.avgEngagementRate ?? 0).toFixed(2)}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
      didParseCell: arabicAwareCellHook(arabicFontLoaded),
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Participants
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.participantsHeading(this.filteredParticipants?.length || 0), y, arabicFontLoaded);

    if (this.filteredParticipants && this.filteredParticipants.length > 0) {
      const participantRows = this.filteredParticipants.map((p: any) => [
        this.getParticipantName(p),
        this.getParticipationDate(p) ? new Date(this.getParticipationDate(p) as string).toLocaleString() : L.na,
        String(this.getParticipantAge(p)),
        this.getParticipantGender(p),
        p.status === 'completed' ? L.statusCompleted : (p.status === 'not_started' ? L.statusNotStarted : L.statusInProgress),
        this.getParticipantLocation(p),
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.participantCol, L.participationDateCol, L.ageCol, L.genderCol, L.statusCol, L.locationCol]],
        body: participantRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noParticipants, 14, y, arabicFontLoaded);
      y += 10;
    }

    // Winners
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.winnersHeading, y, arabicFontLoaded);

    if (this.winnersList && this.winnersList.length > 0) {
      const winnerRows = this.winnersList.map((w: any, i: number) => [
        String(i + 1),
        w._end_user?.name || w.contest?.user_id || L.na,
        this.getParticipantGender(w),
        String(this.getParticipantAge(w)),
        this.getWinnerCompletionTimestamp(w) ? new Date(this.getWinnerCompletionTimestamp(w) as string).toLocaleString() : L.na,
        this.getWinnerPrizeStatus(w),
        w._end_user?.country || w.contest?.location || L.na,
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.rankCol, L.participantCol, L.genderCol, L.ageCol, L.completionTimestampCol, L.prizeStatusCol, L.locationCol]],
        body: winnerRows,
        theme: 'striped',
        headStyles: { fillColor: [8, 80, 65], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noWinners, 14, y, arabicFontLoaded);
    }

    // Footer
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
      drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
    }

    const sanitizedName = (this.name || 'engagement').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
    const fileName = `${sanitizedName}_details.pdf`;
    doc.save(fileName);
  }

  private pdfImageFormat(dataUrl: string): string {
    if (dataUrl.startsWith('data:image/png')) return 'PNG';
    if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
    return 'JPEG';
  }

  // Draws a simplified phone-frame mockup of how the contest appears in the mobile app
  // (banner, name, description, CTA) — there is no existing mobile-preview UI to reuse.
  private async drawMobilePreview(doc: jsPDF, y: number, isAr: boolean = false, arabicFontLoaded: boolean = false): Promise<void> {
    const pageWidth = doc.internal.pageSize.width;
    const frameWidth = 70;
    const frameHeight = 130;
    const x = (pageWidth - frameWidth) / 2;

    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(1.2);
    doc.roundedRect(x, y, frameWidth, frameHeight, 6, 6);
    doc.setFillColor(30, 30, 30);
    doc.roundedRect(x + frameWidth / 2 - 10, y + 2, 20, 3, 1.5, 1.5, 'F');

    const innerX = x + 3;
    const innerWidth = frameWidth - 6;
    let cursor = y + 8;

    const bannerUrl = this.banners && this.banners.length > 0 ? this.banners[0].url : null;
    if (bannerUrl) {
      try {
        const dataUrl = await this.sharedService.getImageDataUrl(bannerUrl);
        if (dataUrl) {
          doc.addImage(dataUrl, this.pdfImageFormat(dataUrl), innerX, cursor, innerWidth, 45);
        }
      } catch {
        // Skip banner if it fails to load/convert.
      }
    }
    cursor += 48;

    const previewText = isAr ? {
      contest: 'الحملة',
      noDescription: 'لا يوجد وصف متاح.',
      participateNow: 'شارك الآن',
    } : {
      contest: 'Campaign',
      noDescription: 'No description available.',
      participateNow: 'Participate Now',
    };

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 20, 20);
    const nameLines = doc.splitTextToSize(this.name || previewText.contest, innerWidth);
    doc.text(nameLines, innerX, cursor);
    cursor += nameLines.length * 4 + 2;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(90, 90, 90);
    const plainDesc = (this.description || '').replace(/<[^>]*>/g, '').trim();
    const descLines = doc.splitTextToSize(plainDesc || previewText.noDescription, innerWidth).slice(0, 8);
    doc.text(descLines, innerX, cursor);

    doc.setFillColor(60, 52, 137);
    doc.roundedRect(innerX, y + frameHeight - 14, innerWidth, 8, 3, 3, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, previewText.participateNow, x + frameWidth / 2, y + frameHeight - 9, arabicFontLoaded, { align: 'center' });
  }

  // Contest configuration PDF: all creation-form fields, Luqta/client logos, a mobile-app
  // preview mockup, and submission metadata (submitted by/via/on, status, approved by).
  async exportConfigurationPDF(): Promise<void> {
    this.sharedService.showLoader();
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      let y = 0;

      const isAr = isArabicLang();
      const arabicFontLoaded = await ensureArabicFont(doc);

      const L = isAr ? {
        headerTitle: 'تفاصيل إعدادات الحملة',
        untitled: 'حملة بدون عنوان',
        submissionDetailsHeading: 'تفاصيل التقديم',
        fieldCol: 'الحقل',
        valueCol: 'القيمة',
        submittedBy: 'مقدم بواسطة',
        submittedVia: 'تم التقديم عبر',
        submittedOn: 'تاريخ التقديم',
        status: 'الحالة',
        approvedBy: 'تمت الموافقة بواسطة',
        notYetApproved: 'لم تتم الموافقة بعد',
        na: 'غير متوفر',
        descriptionHeading: 'الوصف',
        configurationHeading: 'الإعدادات',
        type: 'النوع',
        format: 'الصيغة',
        sharingSetting: 'إعداد المشاركة',
        gender: 'الجنس',
        ageGroup: 'الفئة العمرية',
        location: 'الموقع',
        businessObjective: 'الهدف التجاري',
        numberOfWinners: 'عدد الفائزين',
        drawType: 'نوع السحب',
        noOfUsersSdk: 'عدد المستخدمين (SDK)',
        startDate: 'تاريخ البداية',
        endDate: 'تاريخ النهاية',
        drawDate: 'تاريخ السحب',
        prizesHeading: 'الجوائز',
        prizeNameCol: 'اسم الجائزة',
        levelsHeading: 'مستويات الحملة',
        numCol: '#',
        levelNameCol: 'اسم المستوى',
        typeCol: 'النوع',
        previewHeading: 'معاينة الحملة (تطبيق الجوال)',
        pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
        generated: (date: string) => `تم الإنشاء: ${date}`,
      } : {
        headerTitle: 'Campaign Configuration Details',
        untitled: 'Untitled Campaign',
        submissionDetailsHeading: 'Submission Details',
        fieldCol: 'Field',
        valueCol: 'Value',
        submittedBy: 'Submitted By',
        submittedVia: 'Submitted Via',
        submittedOn: 'Submitted On',
        status: 'Status',
        approvedBy: 'Approved By',
        notYetApproved: 'Not yet approved',
        na: 'N/A',
        descriptionHeading: 'Description',
        configurationHeading: 'Configuration',
        type: 'Type',
        format: 'Format',
        sharingSetting: 'Sharing Setting',
        gender: 'Gender',
        ageGroup: 'Age Group',
        location: 'Location',
        businessObjective: 'Business Objective',
        numberOfWinners: 'Number of Winners',
        drawType: 'Draw Type',
        noOfUsersSdk: 'No. of Users (SDK)',
        startDate: 'Start Date',
        endDate: 'End Date',
        drawDate: 'Draw Date',
        prizesHeading: 'Prizes',
        prizeNameCol: 'Prize Name',
        levelsHeading: 'Campaign Levels',
        numCol: '#',
        levelNameCol: 'Level Name',
        typeCol: 'Type',
        previewHeading: 'Campaign Preview (Mobile App)',
        pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
        generated: (date: string) => `Generated: ${date}`,
      };

      const [luqtaLogo, clientLogo] = await Promise.all([
        this.sharedService.getLuqtaLogoDataUrl(),
        this.sharedService.getClientLogoDataUrl(),
      ]);

      // Header bar with Luqta logo (left) and client logo (right)
      doc.setFillColor(60, 52, 137);
      doc.rect(0, 0, pageWidth, 26, 'F');
      if (luqtaLogo) {
        try { doc.addImage(luqtaLogo, this.pdfImageFormat(luqtaLogo), 10, 4, 18, 18); } catch { }
      }
      if (clientLogo) {
        try { doc.addImage(clientLogo, this.pdfImageFormat(clientLogo), pageWidth - 28, 4, 18, 18); } catch { }
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, L.headerTitle, pageWidth / 2, 15, arabicFontLoaded, { align: 'center' });
      y = 36;

      const cd = this.contests_detail || {};

      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
      y += 8;

      // Submission details
      y = this.pdfSectionHeading(doc, L.submissionDetailsHeading, y, arabicFontLoaded);
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: [
          [L.submittedBy, this.submitted_by?.name ? `${this.submitted_by.name}${this.submitted_by.email ? ' (' + this.submitted_by.email + ')' : ''}` : L.na],
          [L.submittedVia, this.getSubmittedViaLabel() || L.na],
          [L.submittedOn, this.submitted_on || L.na],
          [L.status, this.getStatusLabel(cd.status)],
          [L.approvedBy, this.approved_by?.name ? `${this.approved_by.name}${this.approved_by.email ? ' (' + this.approved_by.email + ')' : ''}` : L.notYetApproved],
        ],
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Description
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.descriptionHeading, y, arabicFontLoaded);
      const plainDesc = (this.description || '').replace(/<[^>]*>/g, '').trim() || L.na;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const descLines = doc.splitTextToSize(plainDesc, pageWidth - 28);
      doc.text(descLines, 14, y);
      y += descLines.length * 5 + 8;

      // Configuration fields (from the contest creation form)
      y = this.pdfEnsureSpace(doc, y, 80);
      y = this.pdfSectionHeading(doc, L.configurationHeading, y, arabicFontLoaded);
      const businessObjective = Array.isArray(cd.business_objective)
        ? cd.business_objective.join(', ')
        : (cd.business_objective || L.na);
      const configRows: string[][] = [
        [L.type, cd.type || L.na],
        [L.format, cd.format_of_contest || L.na],
        [L.sharingSetting, cd.sharingSetting || cd.sharing_setting || L.na],
        [L.gender, cd.gender || L.na],
        [L.ageGroup, cd.age || L.na],
        [L.location, cd.location || L.na],
        [L.businessObjective, businessObjective],
        [L.numberOfWinners, cd.number_of_winners != null ? String(cd.number_of_winners) : L.na],
        [L.drawType, this.getDrawTypeLabel()],
        // Points pool is left out: Luqta allocates points, so it is not the client's field.
        [L.noOfUsersSdk, cd.no_of_users != null ? String(cd.no_of_users) : L.na],
        [L.startDate, this.start_date || L.na],
        [L.endDate, this.end_date || L.na],
        [L.drawDate, this.draw_date || L.na],
      ];
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: configRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Prizes
      if (this._contest_prize && this._contest_prize.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
        const prizeRows = this._contest_prize.map((prize: any) => [
          (arabicFontLoaded ? (prize?.arabic_prize_name || prize?.arabic_name || prize?.name) : prize?.name) || L.na,
          prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.prizeNameCol, L.valueCol]],
          body: prizeRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Levels
      if (this.levels_of_contest && this.levels_of_contest.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);
        const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
          String(i + 1),
          lvl.name || lvl.level_name || `Level ${i + 1}`,
          lvl.level_type || lvl.type || L.na,
          // No points column: Luqta allocates level points, so it is not the client's field.
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.numCol, L.levelNameCol, L.typeCol]],
          body: levelRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Mobile app preview mockup
      y = this.pdfEnsureSpace(doc, y, 140);
      y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
      await this.drawMobilePreview(doc, y, isAr, arabicFontLoaded);

      // Footer
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
        drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
      }

      const sanitizedName = (this.name || 'contest').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
      doc.save(`${sanitizedName}_configuration.pdf`);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to export configuration PDF.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  ngOnInit(): void {
    // Setup debounced search for participants
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyClientFilters();
    });

    const encodedUid = this.route.snapshot.queryParamMap.get('id');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);

      if (storedId) {
        // NOT Number(): a contest id is a uuid, and Number(uuid) is NaN.
        this.contestID = String(storedId);
        this.getContestPerformance(this.contestID);
        this.getContestDetails(this.contestID);
        this.getLevelCompletionMetrics(this.contestID);
        this.checkWebQrCampaign(this.contestID);
      } else {
        this.sharedService.createNotification('error', this.translateService.instant('contestDetails.invalidOrExpiredLink'));
        this.router.navigate(['/home/campaigns']);
      }
    } else {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.noContestIdInUrl'));
      this.router.navigate(['/home/campaigns']);
    }
  }

  private checkWebQrCampaign(contestId: string): void {
    this.qrInstancesLoading = true;
    this.pagesService.getAllById('QR_INSTANCE_LIST', { contest_id: contestId }).subscribe({
      next: (res: any) => {
        const instances = readQrInstances(res);
        this.qrInstances = instances.map((instance: any) => ({
          ...instance,
          contest_id: instance.contest_id ?? contestId,
          short_link: resolveQrInstanceLink(instance, contestId),
        }));
        this.hasQrInstances = instances.length > 0;
        this.qrInstancesLoading = false;
        this.loadQrInstanceParticipants();
      },
      error: () => {
        this.hasQrInstances = false;
        this.qrInstances = [];
        this.qrInstancesLoading = false;
      },
    });
  }

  // Fills the participants table's QR Instance column: the participant list itself carries no
  // instance, so each instance's own participant list says who joined through it.
  private loadQrInstanceParticipants(): void {
    if (!this.qrInstances.length) {
      this.qrInstanceIndex = new Map();
      return;
    }
    loadQrInstanceIndex(this.pagesService, this.qrInstances)
      .pipe(takeUntil(this.destroy$))
      .subscribe((index) => (this.qrInstanceIndex = index));
  }

  getParticipantQrInstance(participant: any): string {
    return qrInstanceOfRow(participant, this.qrInstanceIndex, this.qrInstances, this.contestID)?.label || '';
  }

  getQrInstanceStatusKey(instance: any): 'expired' | 'active' | 'inactive' {
    return qrInstanceStatusKey(instance);
  }

  getQrPlacementLabel(placement: string | undefined): string {
    return placement === 'digital'
      ? this.translateService.instant('webQr.placement.digital')
      : this.translateService.instant('webQr.placement.print');
  }

  copyQrInstanceLink(link: string): void {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      this.sharedService.createNotification('success', this.translateService.instant('webQr.linkCopied'));
    });
  }

  manageQrInstances(): void {
    this.router.navigate(['/home/campaigns/web-qr'], {
      queryParams: { contest_id: this.contestID, contest_name: this.name || '' },
    });
  }

  async getLevelCompletionMetrics(contestId: number | string): Promise<void> {
    this.levelMetricsLoading = true;
    this.levelMetricsError = '';
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEVEL_COMPLETION_METRICS', { contest_id: contestId })
      );
      const payload = response?.data ?? response;
      this.levelMatrixFromServer = payload && Array.isArray(payload.levels) ? payload : null;
      this.syncLevelMatrix();
    } catch (error: any) {
      this.levelMetricsError = 'Level metrics unavailable.';
      this.levelMatrixFromServer = null;
      this.levelMatrix = null;
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch level completion metrics:', error);
      }
    } finally {
      this.levelMetricsLoading = false;
    }
  }

  // The metrics and the participants load side by side, so this runs when either arrives. The server counts a
  // participant whose attempt began as "not started"; their own levels say which level they are playing.
  private syncLevelMatrix(): void {
    this.levelMatrix = reconcileLevelMatrix(this.levelMatrixFromServer, this.participantsList, this.orderedLevelIds());
  }

  // The campaign's level ids in play order (levels_of_contest is sorted by position when the campaign loads).
  private orderedLevelIds(): string[] {
    return (this.levels_of_contest || [])
      .map((level: any) => String(level?.id ?? ''))
      .filter(Boolean);
  }

  // Opens the demographic breakdown drawer for a single level row. The level
  // object already carries by_gender/by_country/by_age_group/by_device_type
  // from the contest_level_completion_metrics response, so no extra fetch.
  openLevelDemographicsDrawer(level: any): void {
    this.selectedLevelMetrics = level;
    this.levelDemographicsDrawerVisible = true;
  }

  closeLevelDemographicsDrawer(): void {
    this.levelDemographicsDrawerVisible = false;
    this.selectedLevelMetrics = null;
  }

  // "unknown" (missing profile field) is rendered as its own labeled bucket
  // rather than dropped, per each dimension's breakdown array.
  getBreakdownLabel(value: any): string {
    if (value === null || value === undefined) return 'Unknown';
    const normalized = String(value).trim();
    if (!normalized || normalized.toLowerCase() === 'unknown') return 'Unknown';
    return normalized;
  }

  getDemographicSections(level: any): Array<{ key: string; title: string; dimensionLabel: string; icon: string; rows: any[]; totalCount: number }> {
    if (!level) return [];

    // Rows are sorted highest-total-first so a later top-N slice (e.g. country) keeps the most relevant groups.
    const buildRows = (entries: any[], dimensionKey: string) =>
      (Array.isArray(entries) ? entries : []).map((entry: any) => {
        const notStarted = entry?.not_started ?? 0;
        const inProgress = entry?.in_progress ?? 0;
        const completed = entry?.completed ?? 0;
        return {
          label: this.getBreakdownLabel(entry?.[dimensionKey]),
          not_started: notStarted,
          in_progress: inProgress,
          completed: completed,
          total: notStarted + inProgress + completed
        };
      }).sort((a, b) => b.total - a.total);

    // Gender and age-group breakdowns are gone — Web QR collects neither.
    const countryRows = buildRows(level.by_country, 'country');
    const deviceTypeRows = buildRows(level.by_device_type, 'device_type');

    return [
      // Country lists can grow large, so only the top 5 by participant count are shown.
      { key: 'country', title: this.translateService.instant('contestDetails.byCountryTop5'), dimensionLabel: this.translateService.instant('contestDetails.country'), icon: 'fa-earth-americas', rows: countryRows.slice(0, 5), totalCount: countryRows.length },
      { key: 'device_type', title: this.translateService.instant('contestDetails.byDeviceType'), dimensionLabel: this.translateService.instant('contestDetails.deviceType'), icon: 'fa-mobile-screen-button', rows: deviceTypeRows, totalCount: deviceTypeRows.length },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editContest() {
    if (!this.isContestEditable()) {
      this.sharedService.createNotification('error', this.translateService.instant(cannotEditMessageKey(this.contests_detail)));
      return;
    }

    const id = this.contestID;
    const uid = uuidv4(); // Generate a unique UID for this contest

    // Store the mapping temporarily (UID → actual numeric ID)
    localStorage.setItem(uid, id.toString());

    // Navigate using the UID instead of the real numeric ID
    this.router.navigate(['/home/campaigns/edit'], { queryParams: { id: uid } });
  }

  isContestEditable(): boolean {
    return !!this.contests_detail?.status && canEditCampaign(this.contests_detail);
  }

  viewContestAnalytics() {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: this.contestID },
    });
  }
  async getContestDetails(id: string) {
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(this.pagesService.getById('GET_SINGLE_CONTEST', id));
      if (response) {
        const contestData = response;
        this.contests_detail = contestData;
        this.name = contestData.name;
        this.created_at = contestData.created_at;
        this.description = contestData.details;

        this.submitted_by = contestData.submitted_by ?? null;
        this.submitted_via = contestData.submitted_via ?? null;
        // submitted_on has no dedicated backend field yet — falls back to created_at.
        this.submitted_on = this.formatDate(contestData.submitted_on ?? contestData.created_at);
        this.approved_by = contestData.approved_by ?? null;

        // Sort levels by their order or creation sequence
        this.levels_of_contest = contestData._levels_of_contest
          .map((lvl: any) => this.normalizeLevelForDisplay(lvl))
          .sort((a: any, b: any) => {
          // Ids are uuids, so subtracting them was NaN and left the order to chance; the
          // backend's `position` is the level number the client chose.
          const pa = Number(a.position ?? a.order);
          const pb = Number(b.position ?? b.order);
          if (Number.isFinite(pa) && Number.isFinite(pb)) {
            return pa - pb;
          }
          return 0;
        });

        this.banners = contestData.banners || [];
        this.bannerLoadFailed = false;
        this.start_date = this.formatDate(contestData.start_date);
    // Kept unformatted for the countdown: the template used to hand getTimeRemaining the
    // already-formatted end date, which no date parser accepts.
    this.end_date_raw = contestData.end_date ?? null;
        this.end_date = this.formatDate(contestData.end_date);
        this.draw_date = this.resolveDrawDate(contestData);
        this._contest_prize = contestData._contest_prize;
        this.loadGiftCardCodes(id);

        this.levels_of_contest.forEach(level => {
          this.showAllVariables[level.id] = false;
        });

        // Pre-load quizzes if any level has quiz type
        const hasQuizLevel = this.levels_of_contest.some((level: any) =>
          (level.level_type || level.type) === 'quiz'
        );
        if (hasQuizLevel && !this.quizzesLoaded) {
          this.getQuizzesRecords();
        }

        // Fetch participants data for this contest
        await this.getContestParticipants(id);
        await this.buildLeaderboard();

        this.applyPerformanceFallbacks();
      }
    } catch (error) {
      this.sharedService.showError(error, 'Failed to fetch campaign details.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  async getContestParticipants(contestId: string) {
    this.participantsLoading = true;
    this.winnersLoading = true;
    try {
      // Use GET request with query parameter instead of path parameter
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('GET_Total_PARTICIPANT', 'contest_id', contestId)
      );

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      if (items.length > 0) {
        const normalizedParticipants = items.map((participant: any) =>
          this.normalizeContestParticipant(participant)
        );

        const sortedParticipants = [...normalizedParticipants].sort(
          (a: any, b: any) =>
            this.getParticipationDateTimestamp(b) - this.getParticipationDateTimestamp(a)
        );

        this.participantsList = sortedParticipants;
        this.filteredParticipants = sortedParticipants;

        // Extract winners from participants
        this.extractWinners();
        this.applyPerformanceFallbacks();
      } else {
        // If response is not an array, set empty arrays
        this.participantsList = [];
        this.filteredParticipants = [];
        this.winnersList = [];
        this.applyPerformanceFallbacks();
      }
    } catch (error: any) {
      // Handle error silently - just set empty arrays
      this.participantsList = [];
      this.filteredParticipants = [];
      this.winnersList = [];
      this.applyPerformanceFallbacks();

      // Don't show error notification for ERROR_CODE_NOT_FOUND
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch participants:', error);
      }
    } finally {
      this.participantsLoading = false;
      this.winnersLoading = false;
      this.syncLevelMatrix();
    }
  }

  private normalizeContestParticipant(participant: any): any {
    if (!participant || typeof participant !== 'object') {
      return participant;
    }

    const normalized = { ...participant };

    const contestCandidate =
      participant?.contest ||
      participant?._contest ||
      participant?.contest_detail ||
      participant?.contestDetail ||
      participant?.contest_details ||
      participant?.contestDetails ||
      this.contests_detail ||
      null;

    if (contestCandidate && !participant?.contest) {
      normalized.contest = contestCandidate;
    }

    const endUserCandidate =
      participant?._end_user ||
      participant?.end_user ||
      participant?.endUser ||
      participant?.user ||
      participant?.participant ||
      null;

    if (endUserCandidate && !participant?._end_user) {
      normalized._end_user = endUserCandidate;
    }

    const statusCandidate =
      participant?.status ||
      participant?.participant_status ||
      participant?.contestParticipant?.status ||
      participant?.contest_participant?.[0]?.status ||
      participant?._contest_participant?.[0]?.status ||
      participant?.contest_participants?.[0]?.status ||
      null;

    if (statusCandidate && !participant?.status) {
      normalized.status = statusCandidate;
    }

    if (statusCandidate && !participant?.participant_status) {
      normalized.participant_status = statusCandidate;
    }

    if (!participant?.levels_detail && participant?.levelsDetail) {
      normalized.levels_detail = participant.levelsDetail;
    }

    if (!participant?.levelsDetail && participant?.levels_detail) {
      normalized.levelsDetail = participant.levels_detail;
    }

    // The server can leave a participant at "not_started" after their first attempt began; their levels already
    // say "in_progress", as does the attempt in the audit records. Show (and filter / export) them as in progress.
    if (toProgressStatus(normalized.status) !== 'in_progress' &&
      effectiveParticipantStatus(normalized, this.orderedLevelIds()) === 'in_progress') {
      normalized.status = 'in_progress';
      normalized.participant_status = 'in_progress';
    }

    return normalized;
  }

  private getParticipationDateTimestamp(participant: any): number {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    if (!participationDate) {
      return 0;
    }

    const timestamp = new Date(participationDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  getParticipationDate(participant: any): string | null {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    return participationDate || null;
  }

  // The app user behind a participation; some payloads send it as a one-item list.
  private getParticipantUser(participant: any): any {
    const user = participant?._end_user || participant?.end_user || null;
    return Array.isArray(user) ? user[0] || null : user;
  }

  getParticipantName(participant: any): string {
    const user = this.getParticipantUser(participant);
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    const name = [
      user?.name,
      fullName,
      participant?.participant_name,
      participant?.userName,
      participant?.name,
      user?.email,
    ].map(value => String(value ?? '').trim()).find(Boolean);
    if (name) return name;

    // App users who signed up with only a phone number have no name, so show who they are by id
    // (the first block of a UUID is enough to tell them apart on screen).
    const id = String(user?.id ?? participant?.participant_id ?? participant?.user_id ?? '').trim();
    return id
      ? this.translateService.instant('contestDetails.participantNumber', { id: id.includes('-') ? id.split('-')[0] : id })
      : 'N/A';
  }

  // The API returns a link level's Arabic instruction as arabic_task_instruction;
  // the other names are older shapes still read so nothing saved before goes blank.
  getLinkTaskInstructionAr(level: any): string {
    return [level?.arabic_task_instruction, level?.link_task_instruction_ar, level?.task_instruction_ar]
      .find((value) => typeof value === 'string' && value.trim() !== '') || '';
  }

  // A participant without a phone number on record shows 0, as on the participants page.
  getParticipantPhone(participant: any): string {
    const user = this.getParticipantUser(participant);
    const phone = [user?.phone_number, user?.phone, user?.mobile, participant?.phone_number, participant?.phone]
      .map(value => String(value ?? '').trim())
      .find(value => value && !['n/a', 'null', 'undefined', '-'].includes(value.toLowerCase()));
    return phone || '0';
  }

  getParticipantGender(participant: any): string {
    return (
      participant?._end_user?.gender ||
      participant?.end_user?.gender ||
      participant?.participant_gender ||
      participant?.participant_gender ||
      participant?.gender ||
      'N/A'
    );
  }

  getParticipantLocation(participant: any): string {
    return (
      participant?._end_user?.country ||
      participant?.end_user?.country ||
      participant?.participant_country ||
      participant?.country ||
      'N/A'
    );
  }

  // Text levels that accept any answer: what each participant typed gets its own column.
  get acceptAnyTextLevels(): any[] {
    return (this.levels_of_contest || []).filter(isAcceptAnyTextLevel);
  }

  // Receipt levels, for the Receipt submissions card. Kept as the same array until the levels
  // change so the card's input does not change on every check.
  private receiptLevelsCache: { from: any[]; levels: ReceiptLevelOption[] } | null = null;
  get receiptLevels(): ReceiptLevelOption[] {
    const all = this.levels_of_contest || [];
    if (this.receiptLevelsCache?.from !== all) {
      this.receiptLevelsCache = {
        from: all,
        levels: all
          .filter((level: any) => level?.level_type === 'receipt' && level?.id)
          .map((level: any) => ({ id: String(level.id), name: level.name || '', arabic_name: level.arabic_name || null })),
      };
    }
    return this.receiptLevelsCache.levels;
  }

  get participantsTableWidth(): string {
    return `${(this.isWebQrCampaign ? 1310 : 1140) + 240 * this.acceptAnyTextLevels.length}px`;
  }

  textLevelName(level: any): string {
    const name = (this.isArabic && level?.arabic_name) || level?.name || level?.arabic_name;
    if (name) return name;
    const position = Number(level?.position) || this.levels_of_contest.indexOf(level) + 1;
    return `${this.translateService.instant('contestDetails.levels.level')} ${position}`;
  }

  textAnswer(participant: any, level: any): TextAnswer {
    return textLevelAnswer(participant, level?.id);
  }

  getParticipantAge(participant: any): string | number {
    const directAge =
      participant?._end_user?.age ??
      participant?.end_user?.age ??
      participant?.participant_age ??
      participant?.age;

    if (directAge !== null && directAge !== undefined && directAge !== '') {
      return directAge;
    }

    const dob =
      participant?._end_user?.date_of_birth ||
      participant?._end_user?.dob ||
      participant?.end_user?.date_of_birth ||
      participant?.end_user?.dob ||
      participant?.participant_dob ||
      participant?.date_of_birth ||
      participant?.dob;

    if (!dob) {
      return 'N/A';
    }

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return 'N/A';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : 'N/A';
  }

  getWinnerCompletionTimestamp(winner: any): string | null {
    const completionTimestamp =
      winner?.completed_at ||
      winner?.completion_date ||
      winner?.completion_timestamp ||
      winner?.updated_at ||
      winner?.modified_at ||
      null;

    return completionTimestamp || null;
  }

  getWinnerPrizeStatus(winner: any): string {
    const participantId = winner?.participant_id ?? winner?._end_user?.id;
    const nestedPrizeHistoryStatus =
      winner?.contest?.prizes?.[0]?.prize_history?.find(
        (history: any) => String(history?.end_user_id) === String(participantId)
      )?.status;

    return (
      winner?.prize_status ||
      winner?.prizeStatus ||
      nestedPrizeHistoryStatus ||
      (winner?.is_winner ? 'Winner' : 'N/A')
    );
  }

  // Display label for the prize status badge — translates only the client-added 'Winner' fallback,
  // leaving raw backend status values (pending, shipped, etc.) untouched so getWinnerPrizeStatusClass matching still works.
  getWinnerPrizeStatusDisplay(winner: any): string {
    const status = this.getWinnerPrizeStatus(winner);
    return status === 'Winner' ? this.translateService.instant('contestDetails.won') : status;
  }

  getWinnerPrizeStatusClass(winner: any): string {
    const rawStatus = (this.getWinnerPrizeStatus(winner) || '').toLowerCase().trim();
    const status = rawStatus.replace(/_/g, ' ');

    if (status === 'pending') return 'prize-status pending';
    if (status === 'processing') return 'prize-status processing';
    if (status === 'ready for dispatch') return 'prize-status ready-dispatch';
    if (status === 'shipped') return 'prize-status shipped';
    if (status === 'in transit') return 'prize-status in-transit';
    if (status === 'out for delivery') return 'prize-status out-delivery';
    if (status === 'delivered' || status === 'completed' || status === 'winner') return 'prize-status delivered';
    if (status === 'returned') return 'prize-status returned';
    if (status === 'rejected' || status === 'cancelled' || status === 'failed') return 'prize-status failed';

    return 'prize-status default';
  }

  /**
   * The codes behind every "unique code per winner" gift card. The contest read
   * never carries them, so they come from the prize pool read the edit form uses.
   * Best-effort: a failure only empties the code list, the rest of the page stands.
   */
  private loadGiftCardCodes(contestId: string): void {
    this.giftCardPools = {};
    this.winnerGiftCards = new Map();
    this.giftCardCodesFailed = false;

    const prizes: any[] = Array.isArray(this._contest_prize) ? this._contest_prize : [];
    if (!prizes.length) return;

    this.giftCardCodesLoading = true;
    this.pagesService.getById('CONTEST_PRIZE_POOL', contestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const pools: Record<string, GiftCardPool> = {};
          const byWinner = new Map<string, GiftCardCode[]>();

          readPrizePools(res).forEach((pool: any) => {
            const prize = prizes.find((p: any) => String(p?.id) === String(pool?.id));
            // The pool is the fresher source for the kind and mode; the contest read is the fallback.
            const kind = { prize_kind: pool?.prize_kind ?? prize?.prize_kind, code_mode: pool?.code_mode ?? prize?.code_mode };
            if (!prize || !isUniqueGiftCard(kind)) return;

            const giftCards = readGiftCardPool(pool);
            pools[giftCards.prizeId] = giftCards;
            giftCards.codes.forEach((code) => {
              if (!code.winnerId) return;
              byWinner.set(code.winnerId, [...(byWinner.get(code.winnerId) ?? []), code]);
            });
          });

          this.giftCardPools = pools;
          this.winnerGiftCards = byWinner;
          this.giftCardCodesLoading = false;
        },
        error: () => {
          this.giftCardCodesFailed = true;
          this.giftCardCodesLoading = false;
        },
      });
  }

  isGiftCardCodePrize(prize: any): boolean {
    return isUniqueGiftCard(prize) || !!this.giftCardPools[String(prize?.id)];
  }

  giftCardPoolOf(prize: any): GiftCardPool | null {
    return this.giftCardPools[String(prize?.id)] ?? null;
  }

  /** Whether the winners table carries the gift card columns. */
  get hasGiftCardCodePrize(): boolean {
    return (this._contest_prize || []).some((prize: any) => this.isGiftCardCodePrize(prize));
  }

  getWinnerGiftCards(winner: any): GiftCardCode[] {
    const seen = new Set<string>();
    return participantIds(winner)
      .flatMap((id) => this.winnerGiftCards.get(id) ?? [])
      .filter((code) => !seen.has(code.id) && !!seen.add(code.id));
  }

  // A winner with no code yet has not claimed one; once the prize's codes run out
  // of date, there is nothing left for them to claim.
  getWinnerGiftCardStatus(winner: any): GiftCardCodeStatus {
    const codes = this.getWinnerGiftCards(winner);
    if (codes.length) {
      return codes.some((code) => code.status === 'claimed') ? 'claimed' : codes[0].status;
    }
    const expired = Object.values(this.giftCardPools)
      .some((pool) => pool.expiresAt && new Date(pool.expiresAt).getTime() < Date.now());
    return expired ? 'expired' : 'not_claimed';
  }

  giftCardStatusClass(status: GiftCardCodeStatus): string {
    if (status === 'claimed') return 'prize-status delivered';
    if (status === 'expired') return 'prize-status failed';
    return 'prize-status pending';
  }

  copyGiftCardCode(code: string): void {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.giftCardCodes.copied'));
    });
  }

  trackGiftCardCode(_: number, code: GiftCardCode): string {
    return code.id;
  }

  extractWinners() {
    this.winnersList = [];

    // Winner source can come from single contest payload OR nested inside participant.contest.
    const contestDraws = Array.isArray(this.contests_detail?.contest_draw)
      ? this.contests_detail.contest_draw
      : Array.isArray(this.participantsList?.[0]?.contest?.contest_draw)
        ? this.participantsList[0].contest.contest_draw
        : [];

    if (!Array.isArray(contestDraws) || contestDraws.length === 0) {
      return;
    }

    // Normalize IDs as strings to avoid number/string mismatch issues.
    const winnerIds = new Set<string>();
    contestDraws.forEach((draw: any) => {
      if (Array.isArray(draw?.winner_of_contest_draw)) {
        draw.winner_of_contest_draw.forEach((winner: any) => {
          if (winner?.winner_id !== null && winner?.winner_id !== undefined) {
            winnerIds.add(String(winner.winner_id));
          }
        });
      }
    });

    if (winnerIds.size === 0) {
      return;
    }

    this.winnersList = this.participantsList.filter((participant: any) => {
      const participantId =
        participant?.participant_id ??
        participant?.user_id ??
        participant?.end_user?.id ??
        participant?._end_user?.id;

      return participantId !== null &&
        participantId !== undefined &&
        winnerIds.has(String(participantId));
    });
  }

  async buildLeaderboard(page: number = 1): Promise<void> {
    if (this.contests_detail?.draw_type !== 'top_points') return;
    this.leaderboardLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEADERBOARD', {
          contest_id: this.contestID,
          page: page - 1,
          per_page: this.leaderboardPerPage
        })
      );
      const items = Array.isArray(response) ? response : (response?.items || response?.data || []);
      this.leaderboardTotal = response?.total ?? response?.itemsReceived ?? items.length;
      this.leaderboardPage = page;
      this.leaderboard = items.map((p: any, i: number) => ({
        ...p,
        status: this.leaderboardRowStatus(p),
        _rank: p?.rank ?? ((page - 1) * this.leaderboardPerPage + i + 1),
        _points: Number(p?.points ?? p?.total_points ?? p?.score ?? 0),
        _name: p?.name ?? p?.participant_name ?? this.getParticipantName(p),
        _gender: p?.gender ?? this.getParticipantGender(p)
      }));
    } catch {
      this.leaderboard = [];
    } finally {
      this.leaderboardLoading = false;
    }
  }

  // A leaderboard row the server still calls "not started" takes the in-progress state its own levels, or the same
  // participant in the participants table, show — so both tables say the same thing.
  private leaderboardRowStatus(row: any): any {
    if (toProgressStatus(row?.status) === 'in_progress') return row?.status;
    if (effectiveParticipantStatus(row, this.orderedLevelIds()) === 'in_progress') return 'in_progress';
    const ids = [row?.participant_id, row?.user_id, row?.end_user_id, row?._end_user?.id]
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map(String);
    const participant = ids.length
      ? (this.participantsList || []).find((p: any) =>
        [p?.participant_id, p?.user_id, p?._end_user?.id].some((id) => id != null && ids.includes(String(id))))
      : null;
    return participant?.status === 'in_progress' && toProgressStatus(row?.status) === 'not_started'
      ? 'in_progress'
      : row?.status;
  }

  getDrawTypeLabel(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.drawTypeTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.drawTypeFirstToComplete'),
      random: this.translateService.instant('contestDetails.drawTypeRandom'),
      random_draw: this.translateService.instant('contestDetails.drawTypeRandom'),
      instant_result: this.translateService.instant('contestDetails.drawTypeInstantResult'),
      manual: this.translateService.instant('contestDetails.drawTypeManual'),
    };
    return map[dt] || this.humanizeCode(dt);
  }

  getTieBreakingRule(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.tieBreakTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.tieBreakFirstToComplete'),
      random: this.translateService.instant('contestDetails.tieBreakRandom'),
      random_draw: this.translateService.instant('contestDetails.tieBreakRandom'),
      manual: this.translateService.instant('contestDetails.tieBreakManual'),
    };
    return map[dt] || '';
  }

  // submitted_via arrives as a backend code (e.g. CLIENT_DASHBOARD) — show a readable label instead.
  getSubmittedViaLabel(): string {
    const via = (this.submitted_via || '').toString().trim();
    if (!via) return '';
    const map: Record<string, string> = {
      client_dashboard: this.translateService.instant('contestDetails.submittedViaClientDashboard'),
      admin_dashboard: this.translateService.instant('contestDetails.submittedViaAdminPanel'),
      admin_panel: this.translateService.instant('contestDetails.submittedViaAdminPanel'),
      api: this.translateService.instant('contestDetails.submittedViaApi'),
      sdk: this.translateService.instant('contestDetails.submittedViaApi'),
    };
    return map[via.toLowerCase()] || this.humanizeCode(via);
  }

  // The level-metrics API reports its own type bucket (e.g. "INFO" for an image level), so prefer
  // the type of the matching level from the engagement itself, then show a translated label.
  getLevelTypeLabel(metricsLevel: any): string {
    if (!metricsLevel) return 'N/A';
    const metricsId = metricsLevel.level_id ?? metricsLevel.id;
    const metricsUid = metricsLevel.level_uid ?? metricsLevel.uid;
    const match = (this.levels_of_contest || []).find((lvl: any) =>
      (metricsId != null && String(lvl.id) === String(metricsId)) ||
      (metricsUid && lvl.uid === metricsUid) ||
      (metricsLevel.level_name && lvl.name === metricsLevel.level_name)
    );
    const type = (match?.level_type || metricsLevel.level_type || '').toString();
    if (!type) return 'N/A';
    const key = `contestDetails.levelTypes.${type.toLowerCase()}`;
    const label = this.translateService.instant(key);
    return label !== key ? label : this.humanizeCode(type);
  }

  // "random_draw" / "CLIENT_DASHBOARD" -> "Random Draw" / "Client Dashboard"
  private humanizeCode(value: string): string {
    if (!value) return '';
    return value
      .toString()
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  }

  // Frontend-only label mapping — raw contests_detail.status is unchanged.
  getStatusLabel(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') {
      return this.translateService.instant('contestDetails.statusPendingApproval');
    }
    const map: Record<string, string> = {
      approved: this.translateService.instant('contestDetails.statusApproved'),
      rejected: this.translateService.instant('contestDetails.statusRejected'),
      draft: this.translateService.instant('contestDetails.statusDraft'),
      completed: this.translateService.instant('contestDetails.statusCompleted'),
    };
    return map[normalized] || (status || 'N/A');
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') return 'submission-status pending';
    if (['approved', 'rejected', 'draft', 'completed'].includes(normalized)) {
      return `submission-status ${normalized}`;
    }
    return 'submission-status default';
  }

  /** Who can take over: anyone who played and is not already a winner (the server refuses the rest). */
  get overrideCandidates(): any[] {
    return (this.participantsList || []).filter((p: any) => !p?.is_winner);
  }

  openOverrideModal(): void {
    this.overrideModalVisible = true;
    this.overrideParticipantId = null;
    this.overrideReason = '';
    this.loadAuditLog();
  }

  closeOverrideModal(): void {
    this.overrideModalVisible = false;
  }

  async loadAuditLog(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('WINNER_OVERRIDE_AUDIT_LOG', 'contest_id', this.contestID)
      );
      this.overrideAuditLog = Array.isArray(response) ? response : (response?.data || response?.items || []);
    } catch {
      this.overrideAuditLog = [];
    }
  }

  async submitOverride(): Promise<void> {
    if (!this.overrideParticipantId || !this.overrideReason.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.selectParticipantAndReason'));
      return;
    }
    this.overrideSubmitting = true;
    try {
      await firstValueFrom(
        this.pagesService.update('WINNER_OVERRIDE', {
          contest_id: this.contestID,
          participant_id: this.overrideParticipantId,
          reason: this.overrideReason.trim()
        })
      );
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.winnerOverriddenSuccess'));
      this.overrideModalVisible = false;
      await this.getContestParticipants(this.contestID);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to override winner.');
    } finally {
      this.overrideSubmitting = false;
    }
  }

  private coerceNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      if (value === null) return null;
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return this.coerceNumber(value.value);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'count')) {
        return this.coerceNumber(value.count);
      }
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[%\,]/g, '').trim();
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private extractNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const parsed = this.coerceNumber(payload[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    return null;
  }

  private extractString(payload: any, keys: string[]): string | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    return null;
  }

  private extractTrendNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) {
        continue;
      }
      const raw = payload[key];
      if (raw && typeof raw === 'object') {
        if (Object.prototype.hasOwnProperty.call(raw, 'trend')) {
          const parsedTrend = this.coerceNumber((raw as any).trend);
          if (parsedTrend !== null) {
            return parsedTrend;
          }
        }
        continue;
      }
      const parsed = this.coerceNumber(raw);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private normalizeRate(value: number | null): number | null {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const abs = Math.abs(value);
    if (abs > 100) {
      return value / 100;
    }
    if (abs > 0 && abs <= 1) {
      return value * 100;
    }
    return value;
  }

  private unwrapPerformanceResponse(response: any): any {
    if (!response) return null;
    const candidate = response?.data ?? response?.result ?? response?.body ?? response;
    if (Array.isArray(candidate)) {
      return candidate[0] ?? null;
    }
    if (candidate?.performance) {
      return candidate.performance;
    }
    if (candidate?.contestPerformance) {
      return candidate.contestPerformance;
    }
    return candidate;
  }

  private resolveTrendClass(label: string | null): string | null {
    if (!label) return null;
    const normalized = label.toLowerCase();
    if (normalized.includes('lower') || normalized.includes('down') || normalized.includes('decrease') || normalized.startsWith('-')) {
      return 'bg-danger';
    }
    if (normalized.includes('higher') || normalized.includes('up') || normalized.includes('increase') || normalized.startsWith('+')) {
      return 'bg-success';
    }
    return 'bg-secondary';
  }

  private buildEmptyPerformance(): ContestPerformance {
    return {
      totalViews: null,
      completedTasks: null,
      completedTasksIsRate: false,
      avgClickRate: null,
      avgCompletionRate: null,
      participationRate: null,
      totalViewsTrend: null,
      completedTasksTrend: null,
      avgClickRateTrend: null,
      participationRateTrend: null,
      totalViewsTrendLabel: null,
      totalViewsTrendClass: null,
      completedTasksTrendLabel: null,
      completedTasksTrendClass: null,
      avgClickRateTrendLabel: null,
      avgClickRateTrendClass: null,
      participationRateTrendLabel: null,
      participationRateTrendClass: null
    };
  }

  private normalizeContestPerformance(response: any): ContestPerformance {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      return this.buildEmptyPerformance();
    }

    const totalViews = this.extractNumber(payload, [
      'total_views', 'totalViews', 'total_view', 'views', 'view', 'total_view_count', 'totalViewCount'
    ]);

    const completedTasks = this.extractNumber(payload, [
      'completed_tasks', 'completedTasks', 'completed', 'completed_count', 'tasks_completed', 'total_completed_tasks'
    ]);

    const avgClickRate = this.extractNumber(payload, [
      'avg_click_rate', 'avgClickRate', 'avg_clicks_rate', 'avg_click_through_rate', 'click_rate', 'clickRate',
      'engagement_rate', 'engagementRate'
    ]);

    const avgCompletionRate = this.extractNumber(payload, [
      'avg_completion_rate', 'avgCompletionRate', 'completion_rate', 'completionRate',
      'average_completion_rate', 'averageCompletionRate'
    ]);

    const participationRate = this.extractNumber(payload, [
      'participation_rate', 'participationRate', 'participation_rate_percent', 'participationPercentage'
    ]);

    const totalViewsTrend = this.extractTrendNumber(payload, [
      'totalViews', 'total_views', 'total_view', 'views', 'view',
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]);
    const completedTasksTrend = this.extractTrendNumber(payload, [
      'completedTasks', 'completed_tasks', 'completed', 'completed_count',
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]);
    const avgClickRateTrend = this.extractTrendNumber(payload, [
      'avgClickRate', 'avg_click_rate', 'averageCTR',
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend', 'averageCTRTrend'
    ]);
    const participationRateTrend = this.extractTrendNumber(payload, [
      'participationRate', 'participation_rate', 'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]);

    const buildTrendLabel = (trend: number | null): string | null => {
      if (trend === null || trend === undefined || Number.isNaN(trend)) return null;
      const direction = trend >= 0 ? 'Higher' : 'Lower';
      return `${Math.abs(trend).toFixed(0)}% ${direction}`;
    };

    const totalViewsTrendLabel = this.extractString(payload, [
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]) ?? buildTrendLabel(totalViewsTrend);
    const completedTasksTrendLabel = this.extractString(payload, [
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]) ?? buildTrendLabel(completedTasksTrend);
    const avgClickRateTrendLabel = this.extractString(payload, [
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend'
    ]) ?? buildTrendLabel(avgClickRateTrend);
    const participationRateTrendLabel = this.extractString(payload, [
      'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]) ?? buildTrendLabel(participationRateTrend);

    const totalViewsTrendClass = totalViewsTrendLabel
      ? this.resolveTrendClass(totalViewsTrendLabel)
      : totalViewsTrend !== null
        ? (totalViewsTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const completedTasksTrendClass = completedTasksTrendLabel
      ? this.resolveTrendClass(completedTasksTrendLabel)
      : completedTasksTrend !== null
        ? (completedTasksTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const avgClickRateTrendClass = avgClickRateTrendLabel
      ? this.resolveTrendClass(avgClickRateTrendLabel)
      : avgClickRateTrend !== null
        ? (avgClickRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const participationRateTrendClass = participationRateTrendLabel
      ? this.resolveTrendClass(participationRateTrendLabel)
      : participationRateTrend !== null
        ? (participationRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;

    const normalizedAvgCompletionRate = this.normalizeRate(avgCompletionRate);
    const normalizedAvgClickRate = this.normalizeRate(avgClickRate);
    const normalizedParticipationRate = this.normalizeRate(participationRate);

    const hasCompletedTasks = completedTasks !== null && completedTasks !== undefined;
    const resolvedCompletedTasks = hasCompletedTasks ? completedTasks : normalizedAvgCompletionRate;
    const completedTasksIsRate = !hasCompletedTasks && normalizedAvgCompletionRate !== null;

    return {
      totalViews,
      completedTasks: resolvedCompletedTasks,
      completedTasksIsRate,
      avgClickRate: normalizedAvgClickRate,
      avgCompletionRate: normalizedAvgCompletionRate,
      participationRate: normalizedParticipationRate,
      totalViewsTrend,
      completedTasksTrend,
      avgClickRateTrend,
      participationRateTrend,
      totalViewsTrendLabel,
      totalViewsTrendClass,
      completedTasksTrendLabel,
      completedTasksTrendClass,
      avgClickRateTrendLabel,
      avgClickRateTrendClass,
      participationRateTrendLabel,
      participationRateTrendClass
    };
  }

  private buildEmptyPerformanceOverview(): OverviewStats {
    return {
      totalContests: 0,
      activeContests: 0,
      pendingContests: 0,
      finishedContests: 0,
      avgClickRate: 0,
      avgCompletionRate: 0,
      avgParticipationRate: 0,
      avgEngagementRate: 0
    };
  }

  private updatePerformanceOverview(response: any): void {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      return;
    }

    const readValue = (obj: any, fallback: number = 0): number => {
      if (obj == null) return fallback;
      if (typeof obj === 'number') return obj;
      if (typeof obj?.value === 'number') return obj.value;
      return fallback;
    };
    const readTrend = (obj: any): number | null => {
      if (obj == null) return null;
      if (typeof obj?.trend === 'number') return obj.trend;
      return null;
    };
    const prevFromTrend = (current: number, trend: number | null): number => {
      if (trend == null || !isFinite(trend)) return 0;
      const denom = 1 + trend / 100;
      if (denom === 0) return 0;
      return current / denom;
    };

    const totalViews = readValue(
      payload?.view,
      readValue(payload?.views, readValue(payload?.totalViews, readValue(payload?.total_views, 0)))
    );
    const totalClicks = readValue(
      payload?.click,
      readValue(payload?.clicks, readValue(payload?.totalClicks, readValue(payload?.total_clicks, 0)))
    );
    const participants = readValue(
      payload?.participants,
      readValue(payload?.totalParticipants, readValue(payload?.total_participants, 0))
    );
    const completed = readValue(
      payload?.completed,
      readValue(payload?.completedTasks, readValue(payload?.completed_tasks, 0))
    );

    const overallSuccessRateRaw = readValue(
      payload?.overallSuccessRate,
      readValue(payload?.overall_success_rate, readValue(payload?.averageClickThroughRate, readValue(payload?.averageCTR, 0)))
    );
    const avgCompletionRaw = readValue(
      payload?.completionRate,
      readValue(payload?.avgCompletionRate, readValue(payload?.averageCompletionRate, readValue(payload?.avg_completion_rate, 0)))
    );
    const avgParticipationRaw = readValue(
      payload?.participationRate,
      readValue(payload?.avgParticipationRate, readValue(payload?.averageParticipationRate, readValue(payload?.avg_participation_rate, 0)))
    );
    const avgEngagementRaw = readValue(
      payload?.engagementRate,
      readValue(payload?.avgEngagementRate, readValue(payload?.averageEngagementRate, readValue(payload?.avg_engagement_rate, 0)))
    );

    const overallSuccessRate = this.normalizeRate(overallSuccessRateRaw) ?? 0;
    const avgCompletion = this.normalizeRate(avgCompletionRaw) ?? 0;
    const avgParticipation = this.normalizeRate(avgParticipationRaw) ?? 0;
    const avgEngagement = this.normalizeRate(avgEngagementRaw) ?? 0;

    const totalTrend = readTrend(payload?.view) ?? readTrend(payload?.views) ?? readTrend(payload?.totalViews);
    const activeTrend = readTrend(payload?.click) ?? readTrend(payload?.clicks) ?? readTrend(payload?.totalClicks);
    const pendingTrend = readTrend(payload?.participants) ?? readTrend(payload?.totalParticipants);
    const finishedTrend = readTrend(payload?.completed) ?? readTrend(payload?.completedTasks);
    const overallTrend = readTrend(payload?.overallSuccessRate) ?? readTrend(payload?.averageClickThroughRate);
    const completionTrend = readTrend(payload?.completionRate) ?? readTrend(payload?.avgCompletionRate) ?? readTrend(payload?.averageCompletionRate);
    const participationTrend = readTrend(payload?.participationRate) ?? readTrend(payload?.avgParticipationRate) ?? readTrend(payload?.averageParticipationRate);
    const engagementTrend = readTrend(payload?.engagementRate) ?? readTrend(payload?.avgEngagementRate) ?? readTrend(payload?.averageEngagementRate);

    this.performanceStats = {
      totalContests: totalViews,
      activeContests: totalClicks,
      pendingContests: participants,
      finishedContests: completed,
      avgClickRate: overallSuccessRate,
      avgCompletionRate: avgCompletion,
      avgParticipationRate: avgParticipation,
      avgEngagementRate: avgEngagement
    };

    this.performancePreviousStats = {
      totalContests: prevFromTrend(totalViews, totalTrend),
      activeContests: prevFromTrend(totalClicks, activeTrend),
      pendingContests: prevFromTrend(participants, pendingTrend),
      finishedContests: prevFromTrend(completed, finishedTrend),
      avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
      avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
      avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
      avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
    };

    this.performanceTrends = {
      totalContests: totalTrend ?? 0,
      activeContests: activeTrend ?? 0,
      pendingContests: pendingTrend ?? 0,
      finishedContests: finishedTrend ?? 0,
      avgClickRate: overallTrend ?? 0,
      avgCompletionRate: completionTrend ?? 0,
      avgParticipationRate: participationTrend ?? 0,
      avgEngagementRate: engagementTrend ?? 0,
      overallSuccessRate: overallTrend ?? 0
    };
  }

  async getContestPerformance(contestId: number | string): Promise<void> {
    this.performanceLoading = true;
    this.performanceError = null;

    try {
      // A contest id is a uuid. The old guard was Number.isFinite(Number(contestId)),
      // which is false for every uuid — so this method threw before making its request
      // and the cards read "Performance data unavailable." on every contest.
      const parsedContestId = String(contestId ?? '').trim();
      if (!parsedContestId) {
        throw new Error('Invalid contest id for performance request.');
      }
      const startDate = this.performanceDateRange?.[0] ?? null;
      const endDate = this.performanceDateRange?.[1] ?? null;
      const params = {
        start_date: startDate ? Math.floor(startDate.getTime() / 1000) : '',
        end_date: endDate ? Math.floor(endDate.getTime() / 1000) : '',
        contest_id: parsedContestId
      };
      const response = await firstValueFrom(
        this.pagesService.getAllById('CONTEST_DETAIL_OVERVIEW', params)
      );
      this.contestPerformance = this.normalizeContestPerformance(response);
      this.updatePerformanceOverview(response);
      this.applyPerformanceFallbacks();
    } catch (error: any) {
      this.contestPerformance = this.buildEmptyPerformance();
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      this.performanceError = 'Performance data unavailable.';
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest performance:', error);
      }
      this.applyPerformanceFallbacks();
    } finally {
      this.performanceLoading = false;
    }
  }

  private getUniqueParticipantsCount(): number | null {
    if (!Array.isArray(this.participantsList) || this.participantsList.length === 0) {
      return null;
    }

    const participantIds = new Set<string>();
    this.participantsList.forEach((p: any) => {
      const id = p?.participant_id ?? p?.user_id ?? p?._end_user?.id ?? p?.id;
      if (id !== null && id !== undefined) {
        participantIds.add(String(id));
      }
    });

    return participantIds.size > 0 ? participantIds.size : this.participantsList.length;
  }

  private getContestTotalViewsFallback(): number | null {
    return this.extractNumber(this.contests_detail, [
      'total_views', 'totalViews', 'views', 'view', 'total_view_count', 'total_view'
    ]);
  }

  private applyPerformanceFallbacks(): void {
    if (this.performanceLoading) return;

    const nextPerformance: ContestPerformance = { ...this.contestPerformance };
    const fallbackViews = this.getContestTotalViewsFallback();
    const fallbackParticipants = this.getUniqueParticipantsCount();

    if ((nextPerformance.totalViews === null || nextPerformance.totalViews === 0) && fallbackViews && fallbackViews > 0) {
      nextPerformance.totalViews = fallbackViews;
    }

    if ((nextPerformance.participationRate === null || nextPerformance.participationRate === 0) &&
      fallbackViews && fallbackViews > 0 &&
      fallbackParticipants && fallbackParticipants > 0) {
      nextPerformance.participationRate = this.normalizeRate((fallbackParticipants / fallbackViews) * 100);
    }

    this.contestPerformance = nextPerformance;
  }

  formatMetric(value: number | null, isPercent: boolean = false, fractionDigits: number = 1): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    if (isPercent) {
      return `${value.toFixed(fractionDigits)}%`;
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  getPercentWidth(value: number | null): number {
    if (value === null || value === undefined || Number.isNaN(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  onPerformanceDateChange(dates: Date[]): void {
    this.performanceDateRange = dates || [];
    if (this.contestID) {
      this.getContestPerformance(this.contestID);
    }
  }

  getTrendBadge(trend: number | null | undefined): { value: number; isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  getPercentageChange(current: number, previous: number): { value: number; isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }
  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  // Get variables to display (limited or all)
  getDisplayedVariables(level: any): any[] {
    if (!level.client_variables || level.client_variables.length === 0) {
      return [];
    }
    const showAll = this.showAllVariables[level.id];
    return showAll ? level.client_variables : level.client_variables.slice(0, this.maxVariablesToShow);
  }
  shouldShowMore(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      !this.showAllVariables[level.id];
  }
  shouldShowLess(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      this.showAllVariables[level.id];
  }
  LevelUID(uid: string): void {
    if (uid) {
      this.clipboard.copy(uid);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUidCopiedSuccess'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.noUidForLevel'));
    }
  }
  getRemainingVariablesCount(level: any): number {
    return level.client_variables ? level.client_variables.length - this.maxVariablesToShow : 0;
  }
  toggleVariablesDisplay(levelId: string): void {
    this.showAllVariables[levelId] = !this.showAllVariables[levelId];
  }
  /**
   * Whatever a date arrived as, as a Date — or null.
   *
   * Three forms are genuinely in play: epoch milliseconds from the older payloads, epoch
   * SECONDS from a couple of the filters, and ISO strings from the contest endpoints. The
   * seconds case is told apart by magnitude — an epoch in seconds for any date this
   * century is a ten-digit number, and read as milliseconds it lands in 1970.
   */
  private toDate(v: number | string | Date | null | undefined): Date | null {
    if (v === null || v === undefined || v === '') return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

    if (typeof v === 'number' || /^\d+$/.test(String(v).trim())) {
      let n = Number(v);
      if (!Number.isFinite(n) || n === 0) return null;
      if (n < 100000000000) n *= 1000; // seconds, not milliseconds
      const d = new Date(n);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? null : d;
  }

  private formatDate(timestamp: number | string | Date | null | undefined): string {
    if (!timestamp) return 'N/A';

    const date = this.toDate(timestamp);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  private resolveDrawDate(contestData: any): string {
    const drawList = Array.isArray(contestData?.contest_draw) ? contestData.contest_draw : [];
    if (drawList.length > 0) {
      const latestDraw = [...drawList].sort(
        (a: any, b: any) => Number(b?.created_at || 0) - Number(a?.created_at || 0)
      )[0];
      if (latestDraw?.created_at) {
        return this.formatDate(latestDraw.created_at);
      }
    }

    return this.formatDate(contestData?.prize_announce_date);
  }

  copyUID(value: string): void {
    if (value) {
      this.clipboard.copy(value);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.copiedToClipboard'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.nothingToCopy'));
    }
  }

  copyLevelId(levelId: number): void {
    if (levelId) {
      this.clipboard.copy(levelId.toString());
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelIdCopied'));
    }
  }

  // Helper method to check if value exists
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  // Toggle edit mode for a level
  onQuizDropdownOpen(isOpen: boolean): void {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords(): void {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: (err: any) => {
        this.sharedService.createNotification('error', err?.error?.message || this.translateService.instant('contestDetails.failedToLoadQuizzes'));
      },
    });
  }

  getQuizTitle(quizId: number): string {
    const quiz = this.quizzesList.find(q => q.id === quizId);
    return quiz?.quiz_title || 'Quiz #' + quizId;
  }

  toggleEditLevel(levelId: string, level: any): void {
    if (!this.isContestEditable()) return;
    if (this.editingLevels[levelId]) {
      // Cancel editing
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } else {
      // Start editing - create form for this level
      this.editingLevels[levelId] = true;
      this.levelForms[levelId] = this.createLevelForm(level);
    }
  }

  // Minimum answers can't drop below the number of questions marked required (floor of 1).
  getSurveyRequiredCount(level: any): number {
    const required = (level?.survey_questions || []).filter((q: any) => q?.is_required === true).length;
    return Math.max(1, required);
  }

  // Create form for editing level
  createLevelForm(level: any): FormGroup {
    const levelType = level.level_type || level.type;
    const isTextLevel = levelType === 'text';

    const form = this.fb.group({
      name: [level.name || '', Validators.required],
      description: [level.description || '', Validators.required],
      arabic_description: [level.arabic_description || '', Validators.required],
      textContent: this.fb.array(
        level.textContent && level.textContent.trim() !== ''
          ? this.splitTextContent(level.textContent).map((text: string) =>
            this.fb.control(text, isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])
          )
          : [this.fb.control('', isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])]
      ),
      qrData: [level.qrData || level.textContent || ''],
      client_webhook_url: [level.client_webhook_url || ''],
      imageFile: [null],
      client_variables: this.fb.array([]),
      quizzes_id: [level.quizzes_id || null],
      min_answers_required: [
        level.min_answers_required || 1,
        levelType === 'survey'
          ? [Validators.required, Validators.min(this.getSurveyRequiredCount(level)), Validators.max(level.survey_questions?.length || 1)]
          : [Validators.min(1)]
      ],
      question_order: [level.question_order || 'fixed'],
      link_task_instruction: [level.link_task_instruction || ''],
      link_task_instruction_ar: [this.getLinkTaskInstructionAr(level)],
      link_task_url: [level.link_task_url || ''],
      link_task_timer: [level.link_task_timer || 0],
    });

    // Set existing image preview if available
    if (level.image && level.image.url) {
      this.levelImagePreviews[level.id] = level.image.url;
    }

    // Pre-load quizzes if level type is quiz
    if ((level.level_type || level.type) === 'quiz' && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }

    // Add existing client variables if available
    if (level.client_variables && level.client_variables.length > 0) {
      const variablesArray = form.get('client_variables') as FormArray;
      level.client_variables.forEach((variable: any) => {
        variablesArray.push(this.fb.group({
          variable_name: [variable.variable_name || '', Validators.required],
          display_name: [variable.display_name || '', Validators.required],
          datatype: [variable.datatype || 'string', Validators.required],
        }));
      });
    }

    return form;
  }

  // Save updated level
  async saveLevel(levelId: string, level: any): Promise<void> {
    if (!this.isContestEditable()) return;
    const form = this.levelForms[levelId];

    if (!form || form.invalid) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.fillRequiredFields'));
      form?.markAllAsTouched();
      return;
    }

    this.sharedService.showLoader();

    try {
      const formValue = form.value;
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: formValue.name,
        description: formValue.description,
        arabic_description: formValue.arabic_description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
        // Null unless this is a quiz level (set below), so a stale quiz never stays on a text level.
        quizzes_id: null,
      };

      // Add type-specific fields
      if (levelType === 'text') {
        const textContentArray = formValue.textContent || [];
        updatePayload.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
      } else if (levelType === 'qr') {
        updatePayload.qrData = formValue.qrData;
        updatePayload.textContent = formValue.qrData;
      } else if (levelType === 'image') {
        // Handle image upload - check preview first (has base64), then file
        const previewImage = this.levelImagePreviews[levelId];
        const imageFile = formValue.imageFile;

        if (previewImage && typeof previewImage === 'string' && previewImage.startsWith('data:image')) {
          // Use base64 from preview (newly uploaded image)
          updatePayload.image = { path: previewImage };
        } else if (imageFile && imageFile instanceof File) {
          // Convert file to base64 if preview not available
          const imageBase64 = await this.fileToBase64(imageFile);
          updatePayload.image = { path: imageBase64 };
        } else if (level.image) {
          // Keep existing image from server
          updatePayload.image = level.image;
        }
      } else if (levelType === 'client_webhook') {
        updatePayload.client_webhook_url = formValue.client_webhook_url || '';
        updatePayload.client_variables = formValue.client_variables || [];
      } else if (levelType === 'quiz') {
        updatePayload.quizzes_id = formValue.quizzes_id || null;
      } else if (levelType === 'survey') {
        updatePayload.min_answers_required = Number(formValue.min_answers_required) || 1;
        updatePayload.question_order = formValue.question_order || 'fixed';
        updatePayload.survey_questions = level.survey_questions || [];
      } else if (levelType === 'link_task') {
        updatePayload.link_task_instruction = formValue.link_task_instruction || '';
        updatePayload.link_task_instruction_ar = (formValue.link_task_instruction_ar || '').trim();
        // The backend's column is arabic_task_instruction; link_task_instruction_ar alone was dropped.
        updatePayload.arabic_task_instruction = updatePayload.link_task_instruction_ar;
        updatePayload.link_task_url = formValue.link_task_url || '';
        updatePayload.link_task_timer = Number(formValue.link_task_timer) || 0;
      } else if (levelType === 'receipt') {
        // The rules are edited in the campaign builder; send them back unchanged so this
        // name/description edit cannot reset them.
        if (level.receipt_rules) updatePayload.receipt_rules = level.receipt_rules;
      } else {
        // For other types, preserve existing values
        updatePayload.client_webhook_url = '';
        updatePayload.client_variables = [];
      }

      // Update level via API
      const levelData = {
        level_details: [updatePayload],
        contest_id: this.contestID,
        // One level edited in place: the server overlays it on the others instead of treating
        // this one-item list as the whole contest, which soft-deleted every other level.
        merge: true,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local data
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        const textContentArray = formValue.textContent || [];
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          name: formValue.name,
          description: formValue.description,
          arabic_description: formValue.arabic_description,
          textContent: textContentArray.filter((text: string) => text.trim() !== '').join(' || '),
          qrData: formValue.qrData,
          client_webhook_url: formValue.client_webhook_url,
          client_variables: formValue.client_variables || [],
          quizzes_id: updatePayload.quizzes_id,
          min_answers_required: formValue.min_answers_required,
          question_order: formValue.question_order,
          link_task_instruction: formValue.link_task_instruction,
          link_task_instruction_ar: updatePayload.link_task_instruction_ar,
          arabic_task_instruction: updatePayload.arabic_task_instruction,
          link_task_url: formValue.link_task_url,
          link_task_timer: formValue.link_task_timer,
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUpdatedSuccess'));
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to update level');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Check if level is in edit mode
  isEditingLevel(levelId: string): boolean {
    return this.editingLevels[levelId] || false;
  }

  // Get form for a level
  getLevelForm(levelId: string): FormGroup | null {
    return this.levelForms[levelId] || null;
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelId: string): FormArray {
    const levelForm = this.getLevelForm(levelId);
    return (levelForm?.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  // Add a new text content field
  addTextContent(levelId: string): void {
    const textContents = this.getTextContents(levelId);
    textContents.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
  }

  // Remove a text content field
  removeTextContent(levelId: string, textIndex: number): void {
    const textContents = this.getTextContents(levelId);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }

  // API responses have been observed returning level fields under different
  // shapes/names (e.g. nested level_details, text_content instead of textContent,
  // type instead of level_type) — normalize so display bindings don't silently
  // fail to find the answer data.
  private normalizeLevelForDisplay(level: any): any {
    const levelRecord = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
    const level_type =
      level?.level_type ||
      levelRecord?.level_type ||
      level?.type ||
      levelRecord?.type ||
      level?.levelType ||
      levelRecord?.levelType ||
      '';
    const textContent =
      level?.textContent ??
      levelRecord?.textContent ??
      level?.text_content ??
      levelRecord?.text_content ??
      level?.text ??
      levelRecord?.text ??
      '';
    return { ...level, level_type, textContent };
  }

  // Split textContent by || for display
  splitTextContent(textContent: string): string[] {
    if (!textContent || textContent.trim() === '') {
      return [];
    }
    return textContent
      .split(/\s*\|\|\s*/)
      .map((text) => text.trim())
      .filter((text) => text !== '');
  }

  // The receipt rules of a receipt level, for the read-only summary.
  receiptRulesOf(level: any): any {
    const rules = level?.receipt_rules;
    return rules && typeof rules === 'object' ? rules : null;
  }

  receiptProductNames(rules: any): string {
    const arabic = isArabicLang();
    return (Array.isArray(rules?.products) ? rules.products : [])
      .map((p: any) => (typeof p === 'string' ? p : (arabic && String(p?.arabic_name ?? '').trim()) || p?.name))
      .filter((name: any) => !!name)
      .join(', ');
  }

  /**
   * The currencies the minimum spend is in. A rule saved without one (the backend keeps a single
   * `currency` and drops the list when several are accepted) is in its campaign countries'
   * currencies, which is what the save defaults to.
   */
  receiptCurrencyLabel(rules: any): string {
    let codes = cleanCurrencies(rules?.currencies);
    if (!codes.length) codes = cleanCurrencies(rules?.currency);
    if (!codes.length) {
      codes = cleanCurrencies(
        this.getCountriesArray(this.contests_detail?.location)
          .map((name) => currencyForCountry(countryCodeForName(name)))
          .filter(Boolean),
      );
    }
    // A campaign in every country would list over a hundred; the first few say enough.
    const shown = codes.slice(0, 4).join(' / ');
    return codes.length > 4 ? `${shown} +${codes.length - 4}` : shown;
  }

  /** A purchase-window day ('2026-11-03') as dd/mm/yy, the same in English and Arabic. */
  receiptDay(value: unknown): string {
    if (value === null || value === undefined || value === '') return '…';
    let day = typeof value === 'string' ? toIsoDate(value) : null;
    if (!day) {
      // Sent as a timestamp or a Date rather than a day: take its local calendar day.
      const date = this.toDate(value as any);
      if (!date) return String(value);
      const pad = (n: number) => String(n).padStart(2, '0');
      day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }
    const [y, m, d] = day.split('-');
    return `${d}/${m}/${y.slice(2)}`;
  }

  // Get edit button text based on level type
  getEditButtonText(level: any): string {
    if (!level) return this.translateService.instant('contestDetails.editLevel');

    const levelType = level.level_type || level.type;

    switch (levelType) {
      case 'text':
        return this.translateService.instant('contestDetails.editTextLevel');
      case 'qr':
        return this.translateService.instant('contestDetails.editQrLevel');
      case 'image':
        return this.translateService.instant('contestDetails.editImageLevel');
      case 'luqta_weebhook':
        return this.translateService.instant('contestDetails.editLuqtaWebhook');
      case 'client_webhook':
        return this.translateService.instant('contestDetails.editClientWebhook');
      case 'client_qr_completion':
        return 'Client QR Completion';
      default:
        return this.translateService.instant('contestDetails.editLevel');
    }
  }

  // Trigger level image input
  triggerLevelImageInput(levelId: string): void {
    const fileInput = document.getElementById(`imageFile_${levelId}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle level image selection
  onLevelImageSelected(event: Event, levelId: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Clear the input so the same file can be picked again after a rejection or a replace.
    input.value = '';
    // The accept filter can be bypassed ("All files" in the picker), so check type and size here too.
    if (file && !isSupportedCampaignImage(file)) {
      this.sharedService.createNotification('error', this.translateService.instant('addContestsValidation.imageFormat'));
      return;
    }
    if (file && file.size > 2 * 1024 * 1024) {
      this.sharedService.createNotification('error', this.translateService.instant('addContestsValidation.imageSize'));
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.levelImagePreviews[levelId] = reader.result;
        const form = this.levelForms[levelId];
        if (form) {
          form.patchValue({ imageFile: file });
        }
        // Show modal after image is uploaded
        this.showImageModal(levelId);
      };
      reader.readAsDataURL(file);
    }
  }

  // Show image preview modal
  showImageModal(levelId: string): void {
    this.isImageModalVisible[levelId] = true;
  }

  // Handle image modal OK
  handleImageOk(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Handle image modal cancel
  handleImageCancel(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Download level image
  downloadLevelImage(levelId: any, level?: any): void {
    const imageUrl = this.levelImagePreviews[levelId];
    if (!imageUrl || typeof imageUrl !== 'string') return;

    const levelName = level?.name || `level_${levelId}`;
    const cleanFileName = `${(levelName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image')}_${Date.now()}.png`;

    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = cleanFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      fetch(imageUrl)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = cleanFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.download = cleanFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    }
  }

  // Save image and keep preview visible
  async saveImageAndShowPreview(levelId: string, level: any): Promise<void> {
    if (!this.isContestEditable()) return;
    const previewImage = this.levelImagePreviews[levelId];

    if (!previewImage || typeof previewImage !== 'string' || !previewImage.startsWith('data:image')) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.uploadImageFirst'));
      return;
    }

    this.sharedService.showLoader();

    try {
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: level.name,
        description: level.description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
        image: { path: previewImage }
      };

      const levelData = {
        level_details: [updatePayload],
        contest_id: this.contestID,
        // See saveLevel: only this level changes, the others are kept as they are.
        merge: true,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local level data immediately with preview image
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          image: { url: previewImage, path: previewImage }
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.imageSavedSuccess'));

      // Refresh contest details from server to get updated data
      await this.getContestDetails(this.contestID);

    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to save image');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Get client variables FormArray for a level
  // Null when the level has no form yet (callers and the template already guard for it).
  getClientVariables(levelId: string): FormArray | null {
    const form = this.levelForms[levelId];
    return (form?.get('client_variables') as FormArray | null) ?? null;
  }

  // Add new variable to client webhook
  addVariable(levelId: string): void {
    const variables = this.getClientVariables(levelId);
    if (variables) {
      variables.push(this.fb.group({
        variable_name: ['', Validators.required],
        display_name: ['', Validators.required],
        datatype: ['string', Validators.required],
      }));
    }
  }

  // Remove variable from client webhook
  removeVariable(levelId: string, index: number): void {
    const variables = this.getClientVariables(levelId);
    if (variables && index >= 0 && index < variables.length) {
      variables.removeAt(index);
    }
  }

  // Helper method to get countries as array from location string
  getCountriesArray(location: string | null | undefined): string[] {
    if (!location || location.trim() === '') {
      return [];
    }
    // Split by comma, semicolon, or pipe and trim whitespace
    const countries = location.split(/[,;|]/).map(c => c.trim()).filter(c => c !== '');
    return countries;
  }

  // Search handler for participants
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  // Open filter modal for participants
  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.applyClientFilters();
  }

  // Handle filter reset
  onFiltersReset(): void {
    this.currentFilters = {};
    this.applyClientFilters();
  }

  private getGenderFilterValues(selectedGender: string | null | undefined): string[] {
    const gender = String(selectedGender || '').trim().toLowerCase();

    if (gender === 'male') {
      return ['male', '\u0630\u0643\u0631'];
    }

    if (gender === 'female') {
      return ['female', '\u0623\u0646\u062b\u0649'];
    }

    if (gender === 'unspecified') {
      return [''];
    }

    if (gender === 'all') {
      return ['male', '\u0630\u0643\u0631', 'female', '\u0623\u0646\u062b\u0649', ''];
    }

    return [];
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }

  // Clear all filters
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        dateRange: null, level: null, status: null, format: null,
        type: null, viewType: null, location: [], gender: null,
        contest: null, age: null, completionRate: null,
        participant_name: null, participant_email: null
      });
    }
    this.applyClientFilters();
  }

  // Apply client-side filtering on participants
  applyClientFilters(): void {
    let filtered = [...this.participantsList];
    const search = this.searchTerm?.trim().toLowerCase();
    const filters = this.currentFilters;

    // Search filter
    if (search) {
      filtered = filtered.filter(p => {
        const name = this.getParticipantName(p).toLowerCase();
        const gender = this.getParticipantGender(p).toLowerCase();
        const location = this.getParticipantLocation(p).toLowerCase();
        const phone = this.getParticipantPhone(p).replace(/[\s-]/g, '');
        const phoneSearch = search.replace(/[\s-]/g, '');
        const answered = this.acceptAnyTextLevels.some(level => {
          const answer = this.textAnswer(p, level);
          return answer.kind === 'answer' && answer.text.toLowerCase().includes(search);
        });
        return name.includes(search) || gender.includes(search) || location.includes(search) ||
          (phone !== '0' && !!phoneSearch && phone.includes(phoneSearch)) || answered;
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(
        p => String(p.status || p.participant_status || '').toLowerCase() === String(filters.status).toLowerCase()
      );
    }

    // Gender filter
    if (filters.gender) {
      const allowedGenderValues = this.getGenderFilterValues(filters.gender).map((value) =>
        value.toLowerCase().trim()
      );
      filtered = filtered.filter(p => {
        const participantGender = this.getParticipantGender(p);
        const normalizedGender = participantGender === 'N/A'
          ? ''
          : String(participantGender || '').toLowerCase().trim();
        return allowedGenderValues.includes(normalizedGender);
      });
    }

    // Location filter
    if (filters.location && Array.isArray(filters.location) && filters.location.length > 0) {
      filtered = filtered.filter(p => {
        const loc = this.getParticipantLocation(p).toLowerCase();
        return filters.location.some((l: string) => loc.includes(l.toLowerCase()));
      });
    }

    this.filteredParticipants = filtered;
  }

  // Open drawer to view participant's level progression
  openLevelsDrawer(participant: any): void {
    this.selectedParticipant = participant;
    // Only the level being played is in progress; the server marks every unfinished level "in_progress".
    const levelStatuses = effectiveLevelStatuses(participant, this.orderedLevelIds());
    // Get levels from the contest's levels_of_contest and map participant's progress
    this.selectedParticipantLevels = this.levels_of_contest.map((level: any, index: number) => {
      // Check if participant has completed this level
      // `levels_detail` carries the level's id as `id`; `level_id` is the older name.
      const isThisLevel = (pl: any) => String(pl?.level_id ?? pl?.id) === String(level.id);
      const participantLevel = participant.levels_detail?.find(isThisLevel) ||
        participant.levelsDetail?.find(isThisLevel);
      return {
        name: level.name || `Level ${index + 1}`,
        status: (participantLevel ? levelStatuses.get(String(level.id)) : '')
          || ((participant.status || participant.participant_status) === 'completed' ? 'completed' : 'pending'),
        points: participantLevel?.points || 0,
        completed_at: participantLevel?.completed_at || null,
        level_type: level.level_type || level.type,
        image: level.image,
        textContent: level.textContent,
        qrData: level.qrData,
        description: level.description
      };
    });
    this.levelsDrawerVisible = true;
  }

  // Close drawer
  closeLevelsDrawer(): void {
    this.levelsDrawerVisible = false;
    this.selectedParticipant = null;
    this.selectedParticipantLevels = [];
  }
}

